import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  collectionGroup,
} from "firebase/firestore";
import metaService from "../../services/integrations/meta";
import { db } from "../../utils/firebase.config";
import { BigIntUtil } from "../../utils/BigIntUtil";
import { createSelector } from 'reselect';
import { determineChatType, getChatIdentifier, getCollectionPaths, getSenderId } from "../../services/firebase/collectionPaths";
import {
  listenToMessagesOptimized,
  listenToWhatsAppMessagesOptimized,
  fetchOptimizedMessages,
  getQueryPerformanceStats
} from "../../services/firebase/queryOptimization";

/**
 * Logging utility for dual write operations during migration phase
 *
 * This utility tracks the success/failure of dual write operations where messages
 * are written to both legacy (sender ID) and new (chat ID) collection paths.
 *
 * @param {string} operation - The operation being performed (e.g., 'sendMessage', 'updateMessage')
 * @param {boolean} success - Whether the operation was successful
 * @param {Error|null} error - Error object if operation failed
 * @param {Object} metadata - Additional metadata for logging
 */
const logDualWriteOperation = (operation, success, error = null, metadata = {}) => {
  const logData = {
    timestamp: new Date().toISOString(),
    operation,
    success,
    error: error ? error.message : null,
    ...metadata
  };

  if (success) {
    console.log(`[DUAL_WRITE_SUCCESS] ${operation}:`, logData);
  } else {
    console.error(`[DUAL_WRITE_FAILURE] ${operation}:`, logData);
  }

  // In a production environment, you might want to send this to an analytics service
  // Example: analytics.track('dual_write_operation', logData);
};

/**
 * Utility function to normalize phone numbers consistently across the application
 *
 * This ensures WhatsApp phone numbers are formatted consistently for use as
 * Firebase collection document identifiers. The normalization removes the leading
 * '+' sign and any spaces to create a clean identifier.
 *
 * @param {string|number} phoneNumber - The phone number to normalize
 * @returns {string} - Normalized phone number without + prefix and spaces
 */
const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.toString().trim().replace(/^\+|\s+/g, '');
};

/**
 * Fallback function using collectionGroup when chat documents approach fails
 * This is the old approach that queries individual message documents
 */
const setupCollectionGroupFallback = (dispatch, selectedPage) => {
  console.log('[FALLBACK] Setting up collectionGroup fallback listener for page:', selectedPage.page_id);

  try {
    // Use collectionGroup to get all messages subcollections under the page
    const messagesQuery = query(
      collectionGroup(db, "messages"),
      orderBy("created_time", "desc")
    );

    console.log('[FALLBACK] Setting up collectionGroup listener');

    const fallbackUnsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const startTime = performance.now();

      // Group messages by sender and get the latest message from each
      const messagesBySender = new Map();

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const docPath = doc.ref.path; // e.g., "pageId/senderId/messages/messageId"
        const pathParts = docPath.split('/');

        // Filter for current page: ${pageId}/${sender}/messages/messageId
        if (pathParts.length >= 4 && pathParts[0] === selectedPage.page_id.toString()) {
          const senderId = pathParts[1];
          const messageData = {
            id: doc.id,
            senderId,
            sender: senderId, // For compatibility with existing code
            ...data,
          };

          // Keep only the latest message per sender
          if (!messagesBySender.has(senderId) ||
            new Date(messageData.created_time) > new Date(messagesBySender.get(senderId).created_time)) {
            messagesBySender.set(senderId, messageData);
          }
        }
      });

      const updatedMessages = Array.from(messagesBySender.values());

      const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
      console.log('[QUERY_PERFORMANCE] fetchLatestMessages (collectionGroup fallback):', {
        messagesCount: updatedMessages.length,
        processingTime,
        pageId: selectedPage.page_id,
        uniqueSenders: messagesBySender.size
      });

      dispatch(setLatestMessages(updatedMessages));
    }, (error) => {
      console.error('[QUERY_ERROR] CollectionGroup fallback listener error:', error);
      dispatch(setLatestMessages([]));
    });

    dispatch(setLatestMessagesUnsubscribe(fallbackUnsubscribe));
    return fallbackUnsubscribe;

  } catch (error) {
    console.error('[FALLBACK_ERROR] Error setting up collectionGroup fallback:', error);
    dispatch(setLatestMessages([]));
    return null;
  }
};

// Helper function for consistent date parsing
const parseDate = (dateString) => {
  if (!dateString) return new Date(0);

  // Handle different date formats
  if (typeof dateString === 'string') {
    // If it contains a space but no 'T', replace space with 'T'
    if (dateString.includes(' ') && !dateString.includes('T')) {
      dateString = dateString.replace(' ', 'T');
    }
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? new Date(0) : date;
};

/**
 * Helper function to extract the latest message for a specific chat ID
 *
 * This function searches through the latest messages array to find messages
 * associated with a specific chat ID. It checks both sender and recipient fields
 * to handle bidirectional conversations and returns the most recent message.
 *
 * Note: This function maintains compatibility with the existing Context API
 * implementation while supporting the new chat ID-based collection structure.
 *
 * @param {string} chatId - The chat identifier to search for
 * @param {Array} latestMessages - Array of latest messages from Firebase
 * @returns {Object|null} - The latest message object or null if not found
 */
/**
 * Extract latest message for a specific chat ID from chat documents
 *
 * Since we're now querying chat documents directly from /pages/{pageId}/chats/,
 * each document IS a chat with its latest message data. However, there's a mismatch:
 * - Chat list uses participant IDs (e.g., "8120591671387459")
 * - Firebase documents use thread IDs (e.g., "t_370312359377133")
 *
 * We need to match using multiple strategies to handle this mismatch.
 *
 * @param {string} chatId - The participant/chat ID to search for
 * @param {Array} chatDocuments - Array of chat documents from Firebase
 * @returns {Object|null} - The chat document with latest message data or null if not found
 */
const extractLatestMessage = (chatId, chatDocuments) => {
  console.log('[EXTRACT_LATEST_MESSAGE] Called with:', {
    chatId,
    chatIdType: typeof chatId,
    chatDocumentsCount: chatDocuments?.length
  });

  if (!chatDocuments || !Array.isArray(chatDocuments)) {
    console.log('[EXTRACT_LATEST_MESSAGE] No chat documents or not array:', chatDocuments);
    return null;
  }

  // Convert chatId to string for consistent comparison
  const chatIdStr = chatId?.toString();

  // Strategy 1: Direct participant ID match (correct approach)
  // Firebase documents should be stored with participant IDs as document IDs
  let chatDocument = chatDocuments.find(chat => chat?.id?.toString() === chatIdStr);

  // Strategy 2: Handle legacy thread ID documents (temporary fix)
  // Some documents might have been created with thread IDs (t_ prefix) - these should be migrated
  if (!chatDocument) {
    // Check if any documents have thread ID format that might correspond to this participant
    chatDocument = chatDocuments.find(chat => {
      const docId = chat?.id?.toString();
      // If document ID starts with 't_', it's a thread ID (legacy format)
      if (docId?.startsWith('t_')) {
        console.warn(`[EXTRACT_LATEST_MESSAGE] Found legacy thread ID document: ${docId} - should be migrated to participant ID`);
        // For now, try to match by sender/recipient fields in the document
        const senderMatch = chat?.sender?.toString() === chatIdStr;
        const recipientMatch = chat?.recipient?.toString() === chatIdStr;
        return senderMatch || recipientMatch;
      }
      return false;
    });

    if (chatDocument) {
      console.log(`[EXTRACT_LATEST_MESSAGE] Found match using legacy thread ID document with sender/recipient matching`);
    }
  }

  // Strategy 3: Fallback - match by sender/recipient fields regardless of document ID
  if (!chatDocument) {
    chatDocument = chatDocuments.find(chat => {
      const senderMatch = chat?.sender?.toString() === chatIdStr;
      const recipientMatch = chat?.recipient?.toString() === chatIdStr;
      return senderMatch || recipientMatch;
    });

    if (chatDocument) {
      console.log(`[EXTRACT_LATEST_MESSAGE] Found match using sender/recipient fields fallback`);
    }
  }

  console.log(`[EXTRACT_LATEST_MESSAGE] Search result for chatId ${chatIdStr}:`, {
    found: !!chatDocument,
    matchedDocumentId: chatDocument?.id,
    message: chatDocument?.message?.substring(0, 50),
    updated_time: chatDocument?.updated_time,
    created_time: chatDocument?.created_time,
    totalChatsSearched: chatDocuments.length,
    availableChatIds: chatDocuments.slice(0, 5).map(chat => chat?.id),
    sampleDocumentFields: chatDocuments[0] ? {
      id: chatDocuments[0].id,
      sender: chatDocuments[0].sender,
      recipient: chatDocuments[0].recipient
    } : null
  });

  // Return the chat document directly - it already contains all the latest message data
  const result = chatDocument ? {
    ...chatDocument,
    // Ensure we have the standard fields expected by the UI
    message: chatDocument.message,
    message_id: chatDocument.message_id || chatDocument.last_message_id,
    sender_name: chatDocument.sender_name,
    created_time: chatDocument.updated_time || chatDocument.created_time,
  } : null;

  console.log('[EXTRACT_LATEST_MESSAGE] Final result:', {
    found: !!result,
    message: result?.message?.substring(0, 50),
    created_time: result?.created_time
  });

  return result;
};

// Helper function to format timestamp (same as Context API)
const formatTimestamp = (timestamp, currentDate) => {
  if (!timestamp) return "";

  const messageDate = new Date(timestamp);
  const messageDay = messageDate.toDateString();
  const currentDay = currentDate.toDateString();

  if (messageDay === currentDay) {
    return messageDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    const yesterday = new Date(currentDate);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDay = yesterday.toDateString();

    if (messageDay === yesterdayDay) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  }
};

const parsePhoneNumber = (phoneNumber) => {
  const numericPhoneNumber = phoneNumber.replace(/\D/g, "");
  return "+" + numericPhoneNumber;
};

// Async thunks for API calls
export const fetchLatestMessages = createAsyncThunk(
  "metaBusinessSuite/fetchLatestMessages",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState().metaBusinessSuite;
      const selectedPage = state.selectedPage;

      if (!selectedPage?.page_id) {
        return { unsubscribe: null };
      }

      // Clean up any existing listener first to avoid multiple open channels
      const prevUnsub = state.latestMessagesUnsubscribe;
      if (prevUnsub) {
        try {
          prevUnsub();
        } catch (e) {
          console.warn("failed to unsubscribe previous listener", e);
        }
      }

      // Use chat documents approach as primary method (not fallback)
      // Query the correct Firebase path: /pages/{pageId}/chats/
      // Each document in this collection IS a chat document with latest message data
      let unsubscribe = null;

      try {
        const chatsCollectionPath = `pages/${selectedPage.page_id}/chats`;
        const chatsQuery = query(
          collection(db, ...chatsCollectionPath.split('/')),
          orderBy("updated_time", "desc")
        );

        console.log(`[FETCH_LATEST_MESSAGES] Setting up chat documents listener at: ${chatsCollectionPath}`);

        // Set up listener for chat documents (contains latest message data directly)
        unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
          const startTime = performance.now();

          // Each document is a chat document containing latest message data
          const chatDocuments = snapshot.docs.map((doc) => ({
            id: doc.id, // This should be the participant ID
            ...doc.data(), // Contains message, created_time, sender, recipient, etc.
          }));

          const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
          console.log('[QUERY_PERFORMANCE] fetchLatestMessages (chat documents):', {
            chatsCount: chatDocuments.length,
            processingTime,
            pageId: selectedPage.page_id,
            sampleChat: chatDocuments[0] ? {
              id: chatDocuments[0].id,
              hasThreadIdFormat: chatDocuments[0].id?.startsWith('t_'),
              message: chatDocuments[0].message?.substring(0, 50),
              updated_time: chatDocuments[0].updated_time,
              sender: chatDocuments[0].sender,
              recipient: chatDocuments[0].recipient
            } : null,
            allChatIds: chatDocuments.slice(0, 5).map(doc => ({
              id: doc.id,
              isThreadId: doc.id?.startsWith('t_')
            }))
          });

          // Sort chats by updated_time before dispatching (for real-time sorting)
          const sortedChats = [...chatDocuments].sort((a, b) => {
            const timeA = new Date(a.updated_time || a.created_time || 0);
            const timeB = new Date(b.updated_time || b.created_time || 0);
            return timeB - timeA; // Descending order (newest first)
          });

          console.log('[REALTIME_CHATS_DEBUG] Received', chatDocuments.length, 'chats from Firebase, dispatching to Redux');
          console.log('[REALTIME_CHATS_DEBUG] Sorted', sortedChats.length, 'chats by updated_time');

          dispatch(setLatestMessages(sortedChats));
        }, (error) => {
          console.error('[QUERY_ERROR] Chat documents listener error:', error);
          dispatch(setLatestMessages([]));

          // If there's an error, we could fallback to the old collectionGroup approach
          if (error.message.includes('index') || error.message.includes('permission')) {
            console.warn('[FIREBASE_ERROR] Chat documents query failed, trying collectionGroup fallback');
            setupCollectionGroupFallback(dispatch, selectedPage);
          } else {
            dispatch(setLatestMessages([])); // Clear messages on other errors
          }
        });

      } catch (error) {
        console.error('[QUERY_ERROR] Error setting up chat documents listener:', error);
        // Fallback to collectionGroup approach
        setupCollectionGroupFallback(dispatch, selectedPage);
      }

      // Save listener reference in state
      dispatch(setLatestMessagesUnsubscribe(unsubscribe));

      return { unsubscribe };
    } catch (error) {
      console.error("Error setting up latest messages listener:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "metaBusinessSuite/fetchMessages",
  async ({ thread }, { rejectWithValue, getState, dispatch }) => {
    if (!thread) {
      return { messages: [] };
    }
    const state = getState().metaBusinessSuite;
    try {
      const result = await metaService.getMessagesForChatApi({
        ...state.selectedPage,
        access_token: state?.selectedPage?.page_token,
        user_id: thread.id,
      });
      
      dispatch(setSelectedChatStatus(result?.status))

      // Check if response has data property (new Meta Business Suite format)
      let messagesData;
      let paginationUrl = null;
      
      if (result && typeof result === 'object' && result.data && Array.isArray(result.data)) {
        // New format with pagination support
        messagesData = result.data;
        paginationUrl = result.paging?.next || null;
        
        // Store pagination URL in Redux state
        if (paginationUrl) {
          dispatch(setMessagesPaginationUrl(paginationUrl));
        } else {
          dispatch(resetMessagesPagination());
        }
      } else {
        // Legacy format - direct array of messages, no pagination
        messagesData = Array.isArray(result) ? result : [];
        dispatch(resetMessagesPagination());
      }
      
      messagesData.sort(
        (a, b) => parseDate(a.created_time) - parseDate(b.created_time),
      );

      // Determine chat type and get simplified collection paths
      const chatType = determineChatType(thread);

      if (!state?.selectedPage?.page_id) {
        console.error('fetchMessages: No page selected, cannot determine collection paths');
        return { messages: [] };
      }

      // Use simplified path structure: ${pageId}/${sender}/messages
      const collectionPaths = getCollectionPaths(thread, chatType, state.selectedPage.page_id);

      console.log('fetchMessages - Using simplified collection paths:', {
        chatType,
        messagesPath: collectionPaths.messages,
        commentsPath: collectionPaths.comments,
        threadId: thread.id,
        pageId: state.selectedPage.page_id
      });

      // Get sender ID for consistent identification
      const senderId = getSenderId(thread, chatType);

      console.log('fetchMessages - Using sender ID:', {
        senderId,
        threadId: thread.id,
        chatType
      });

      const messageFromFirestore = state?.latestMessages
        ? extractLatestMessage(senderId, state?.latestMessages)
        : null;

      let firestoreMessages = [];
      let activeCollectionPath = null;
      let unsubscribeNested = null;

      if (messageFromFirestore && messageFromFirestore.id) {
        // Use simplified path structure: ${pageId}/${sender}/messages
        if (collectionPaths.messages) {
          const startTime = performance.now();

          try {
            console.log(`[QUERY_OPTIMIZATION] fetchMessages - Using simplified path:`, collectionPaths.messages);

            const nestedCollectionRef = collection(db, ...collectionPaths.messages.split('/'));
            const nestedQuery = query(
              nestedCollectionRef,
              orderBy("created_time", "asc"),
            );

            const firestoreSnapshot = await getDocs(nestedQuery);
            firestoreMessages = firestoreSnapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                created_time: data?.created_time,
                from: {
                  id: new BigIntUtil(data?.sender)?.value,
                  email: new BigIntUtil(data?.sender)?.value,
                },
                message: data?.message,
                type: data?.type,
              };
            });

            activeCollectionPath = collectionPaths.messages;

            const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
            console.log(`[QUERY_PERFORMANCE] fetchMessages (simplified path):`, {
              messagesCount: firestoreMessages.length,
              processingTime,
              path: collectionPaths.messages
            });

            // Set up real-time listener for the simplified path
            console.log(`[FIREBASE_LISTENER] Setting up listener for path: ${collectionPaths.messages}`);

            unsubscribeNested = onSnapshot(nestedQuery, (snapshot) => {
              const startTime = performance.now();

              console.log(`[FIREBASE_LISTENER] Listener triggered for ${collectionPaths.messages}:`, {
                totalDocs: snapshot.docs.length,
                changes: snapshot.docChanges().length,
                changeTypes: snapshot.docChanges().map(change => `${change.type}: ${change.doc.id}`)
              });

              // Log each change for debugging
              snapshot.docChanges().forEach((change) => {
                console.log(`[FIREBASE_LISTENER] Document ${change.type}:`, {
                  id: change.doc.id,
                  data: change.doc.data(),
                  path: change.doc.ref.path
                });
              });

              const liveMessages = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                  id: doc.id,
                  created_time: data?.created_time,
                  from: {
                    id: new BigIntUtil(data?.sender)?.value,
                    email: new BigIntUtil(data?.sender)?.value,
                  },
                  message: data?.message,
                  type: data?.type,
                };
              });

              const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
              console.log(`[QUERY_PERFORMANCE] fetchMessages real-time (simplified path):`, {
                messagesCount: liveMessages.length,
                changes: snapshot.docChanges().length,
                processingTime,
                path: collectionPaths.messages
              });

              console.log(`[FIREBASE_LISTENER] Dispatching updateMessages with ${liveMessages.length} messages`);
              dispatch(updateMessages(liveMessages));
            }, (error) => {
              console.error('[QUERY_ERROR] fetchMessages - Firebase listener error for simplified path:', error);
            });

          } catch (error) {
            const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
            console.error(`[QUERY_ERROR] fetchMessages - Error fetching from simplified path:`, {
              error: error.message,
              processingTime,
              path: collectionPaths.messages
            });
            firestoreMessages = [];
            activeCollectionPath = null;
          }
        }

        // Save unsubscribe function for cleanup
        if (unsubscribeNested) {
          dispatch(setMessagesSnapshotUnsubscribe(unsubscribeNested));
        }
      }

      console.log('fetchMessages - Final result:', {
        nestedMessagesCount: messagesData.length,
        firestoreMessagesCount: firestoreMessages.length,
        activeCollectionPath,
        messageFromFirestore: !!messageFromFirestore,
        hasPagination: !!paginationUrl
      });

      return {
        nestedMessages: messagesData,
        messages: firestoreMessages,
        messageFromFirestore,
        activeCollectionPath,
        paginationUrl
      };
    } catch (error) {
      console.error("Error fetching messages:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchWhatsAppMessages = createAsyncThunk(
  "metaBusinessSuite/fetchWhatsAppMessages",
  async ({ thread, selectedPhone }, { rejectWithValue, dispatch, getState }) => {
    if (!thread || !selectedPhone) {
      return { apiMessages: [] };
    }

    try {
      // Fetch messages from API
      const result = await metaService.getMessagesForWhatsappChatApi({
        id: thread?.id,
        signal: null,
      });

      if (result?.data) {
        // Get the messages from the API response
        const apiMessages = result.data;

        // Update the selectedWhatsappChat with the latest timestamp
        if (apiMessages.length > 0) {
          const latestMessage = apiMessages.sort((a, b) =>
            new Date(b.created_at || b.timestamp) - new Date(a.created_at || b.timestamp)
          )[0];

          // Update the selected WhatsApp chat with the new timestamp
          dispatch(updateSelectedWhatsappChat((prevChat) => {
            if (!prevChat) return prevChat;
            const updatedChat = {
              ...prevChat,
              updated_at: latestMessage.created_at || latestMessage.timestamp || new Date().toISOString()
            };
            return updatedChat;
          }));
        }

        // Sort API messages by timestamp
        apiMessages.sort((a, b) => new Date(a.created_at || a.timestamp) - new Date(b.created_at || b.timestamp));

        // Transform API messages to match our format (same as Context API)
        const formattedApiMessages = apiMessages.map(msg => {
          const formattedMsg = {
            id: msg.id || `api_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            message_id: msg.id || msg.message_id,
            created_time: msg.created_at || msg.timestamp,
            // Preserve the message exactly as received, including all spaces
            message: msg.message,
            // Add URL field for image messages
            url: msg.type === "image" ? msg.url : msg.message,
            type: msg.type || "text",
            sender: msg.from === selectedPhone?.id ? normalizePhoneNumber(selectedPhone?.display_phone_number) : normalizePhoneNumber(thread?.sender_phone_number),
            recipient: msg.from === selectedPhone?.id ? normalizePhoneNumber(thread?.sender_phone_number) : normalizePhoneNumber(selectedPhone?.display_phone_number)
          };

          return formattedMsg;
        });

        // Set initial messages from API immediately
        dispatch(setWhatsappChatMessages(formattedApiMessages));

        // Also update the chat list with the latest message
        if (formattedApiMessages.length > 0) {
          const latestMessage = formattedApiMessages[formattedApiMessages.length - 1];

          // Update the selected WhatsApp chat with the latest message
          dispatch(updateSelectedWhatsappChat((prevChat) => {
            if (!prevChat) return prevChat;
            return {
              ...prevChat,
              last_message: {
                message: latestMessage.message,
                from: latestMessage.sender,
                type: latestMessage.type || "text",
                message_id: latestMessage.message_id,
                timestamp: Math.floor(new Date(latestMessage.created_time).getTime() / 1000).toString(),
                created_at: latestMessage.created_time,
                updated_at: latestMessage.created_time,
                url: latestMessage.url || null
              },
              updated_time: latestMessage.created_time,
            };
          }));
        }

        // Use simplified path structure for WhatsApp messages
        const chatType = 'whatsapp';

        // Get selectedPage from Redux state
        const state = getState().metaBusinessSuite;
        const selectedPage = state.selectedPage;

        const collectionPaths = getCollectionPaths(thread, chatType, selectedPage?.page_id);

        console.log("FetchWhatsAppMessages - Using simplified path structure:", {
          rawPhoneNumber: thread?.sender_phone_number,
          messagesPath: collectionPaths.messages,
          commentsPath: collectionPaths.comments,
          pageId: selectedPage?.page_id,
          thread
        });

        // Set up Firestore listener for real-time updates using simplified path structure
        let unsubscribe = null;

        if (collectionPaths.messages) {
          try {
            const nestedCollectionRef = collection(db, ...collectionPaths.messages.split('/'));
            const nestedQuery = query(
              nestedCollectionRef,
              orderBy("created_time", "asc")
            );

            console.log(`[QUERY_OPTIMIZATION] Setting up WhatsApp listener for path: ${collectionPaths.messages}`);

            unsubscribe = onSnapshot(nestedQuery, (snapshot) => {
              const startTime = performance.now();

              console.log("[QUERY_PERFORMANCE] Firebase listener triggered for WhatsApp. Changes:", snapshot.docChanges().length);

              // Log document changes for debugging
              snapshot.docChanges().forEach((change) => {
                console.log(`Document ${change.type}:`, change.doc.id, change.doc.data());
              });

              const nestedMessages = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                  id: doc.id,
                  message_id: data?.message_id || doc.id,
                  created_time: data?.created_time,
                  message: data?.message,
                  type: data?.type || "text",
                  sender: data?.sender,
                  sender_name: data?.sender_name,
                  recipient: data?.recipient,
                  url: data?.url || null
                };
              });

              const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
              console.log("[QUERY_PERFORMANCE] WhatsApp Firebase messages processed:", {
                messagesCount: nestedMessages.length,
                changes: snapshot.docChanges().length,
                processingTime,
                path: collectionPaths.messages
              });

              // If we have new messages, update the selectedWhatsappChat timestamp
              if (nestedMessages.length > 0) {
                const latestMessage = nestedMessages.sort((a, b) =>
                  new Date(b.created_time) - new Date(a.created_time)
                )[0];

                dispatch(updateSelectedWhatsappChat((prevChat) => {
                  if (!prevChat) return prevChat;
                  return {
                    ...prevChat,
                    updated_at: latestMessage.created_time
                  };
                }));
              }

              // Update whatsappChatMessages state – merging & sorting handled inside reducer
              dispatch(updateWhatsappChatMessages(nestedMessages));
            }, (error) => {
              console.error("[QUERY_ERROR] Firebase listener error for WhatsApp messages:", error);
              // Graceful degradation - continue without real-time updates
              console.warn("WhatsApp messages will not receive real-time updates due to listener error");
            });
          } catch (error) {
            console.error("[QUERY_ERROR] Error setting up WhatsApp Firebase listener:", error);
            // Graceful degradation - continue without real-time updates
            unsubscribe = null;
          }
        } else {
          console.warn("[QUERY_OPTIMIZATION] Unable to set up WhatsApp Firebase listener - invalid path");
        }

        const returnValue = {
          apiMessages: formattedApiMessages,
          updatedChat: thread,
          unsubscribe
        };

        console.log("FetchWhatsAppMessages returning:", {
          apiMessagesCount: formattedApiMessages.length,
          hasUnsubscribe: !!unsubscribe,
          messagesPath: collectionPaths.messages
        });

        return returnValue;
      } else {
        return { apiMessages: [], updatedChat: thread, unsubscribe: null };
      }
    } catch (error) {
      console.error("Error fetching WhatsApp messages:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch paginated messages using the next URL from Meta Business Suite API
export const fetchMessagesPagination = createAsyncThunk(
  "metaBusinessSuite/fetchMessagesPagination",
  async ({ url }, { rejectWithValue, getState }) => {
    try {
      const state = getState().metaBusinessSuite;
      
      if (!url) {
        return rejectWithValue("No pagination URL provided");
      }

      // Use meta service for API call
      const result = await metaService.getMessagesPaginationApi({
        url,
        ...state.selectedPage,
        access_token: state?.selectedPage?.page_token,
      });

      // Sort messages by created_time in ascending order (oldest first for pagination)
      if (result.data && Array.isArray(result.data)) {
        result.data.sort(
          (a, b) => new Date(a.created_time) - new Date(b.created_time)
        );
      }

      return {
        messages: result.data || [],
        nextUrl: result.paging?.next || null,
        hasMore: !!result.paging?.next,
      };
    } catch (error) {
      console.error("Error fetching paginated messages:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch WhatsApp latest messages with Firebase listener
export const fetchWhatsAppLatestMessages = createAsyncThunk(
  "metaBusinessSuite/fetchWhatsAppLatestMessages",
  async ({ selectedPhone }, { rejectWithValue, getState, dispatch }) => {
    try {
      if (!selectedPhone) {
        return null;
      }

      // Get the business phone number in a consistent format
      const businessPhoneNumber = selectedPhone?.display_phone_number.trim().replace(/^\+|\s+/g, '');

      // Query WhatsApp documents where the sender or recipient matches the selected phone
      const whatsAppQuery = query(
        collection(db, "whatsApp"),
        orderBy("created_time", "desc")
      );

      // Set up polling to check for new messages every 10 seconds
      const startPolling = () => {
        // Clean up any existing polling interval
        const state = getState().metaBusinessSuite;
        if (state.pollingInterval) {
          clearInterval(state.pollingInterval);
        }

        // Store the polling interval in Redux state (null for now since polling is disabled)
        dispatch(setPollingInterval(null));
      };

      return new Promise((resolve) => {
        const unsubscribe = onSnapshot(whatsAppQuery, (snapshot) => {
          const firebaseChats = snapshot.docs
            .map((doc) => {
              const data = doc.data();

              // Only include chats where this business phone is the sender or recipient
              if (data.sender !== businessPhoneNumber && data.recipient !== businessPhoneNumber) {
                return null;
              }

              // The document ID is the customer's phone number
              const customerPhoneNumber = doc.id;
              // For chat display, we always want to show the customer's name/number
              const customerName = data.sender_name || customerPhoneNumber;

              // Determine the correct sender_name based on who sent the message
              let correctSenderName;
              if (data.sender === businessPhoneNumber) {
                // Business sent the message, use business name
                correctSenderName = data.sender_name || "Business";
              } else {
                // Customer sent the message, use customer name or phone number
                // For incoming messages from customers, we need to preserve their name
                correctSenderName = data.sender_name || customerPhoneNumber;
              }

              const firebaseChat = {
                // Store the original API ID if available, otherwise use document ID or generate one
                originalApiId: data.originalApiId || doc.id || `temp_${Date.now()}`, // Ensure we always have an ID
                sender_phone_number: customerPhoneNumber,
                // Always use customer name for chat display
                sender_name: customerName,
                // Store the actual sender name separately for the last message
                last_message_sender_name: correctSenderName,
                ...data,
                created_time: data.created_time,
                updated_time: data.created_time,
                created_at: data.created_time,
                updated_at: data.created_time,
                last_message: {
                  message: data.message,
                  from: data.sender,
                  type: data.type || "text",
                  message_id: data.message_id,
                  timestamp: Math.floor(new Date(data.created_time).getTime() / 1000).toString(),
                  created_at: data.created_time,
                  updated_at: data.created_time,
                  url: data.url || null
                },
                // Add a flag to identify this as a Firebase document
                fromFirebase: true
              };
              return firebaseChat;
            })
            .filter(chat => chat !== null); // Remove null entries

          // Dispatch the Redux action to update the state
          dispatch(setWhatsappChats(firebaseChats));

          // Also update latest messages for the merged selector - FIXED: Merge instead of replace
          const latestMessages = firebaseChats.map(chat => ({
            id: chat.sender_phone_number,
            sender: chat.sender,
            sender_name: chat.sender_name,
            recipient: chat.recipient,
            message: chat.message,
            message_id: chat.message_id,
            created_time: chat.created_time,
            updated_time: chat.created_time,
            created_at: chat.created_time,
            type: chat.type || "text",
            url: chat.url || null
          }));

          // Merge with existing latest messages instead of replacing
          dispatch(updateLatestMessages((prevLatestMessages) => {
            if (!prevLatestMessages) return latestMessages;

            // Create a map of existing messages by ID for quick lookup
            const existingMessagesMap = new Map();
            prevLatestMessages.forEach(msg => {
              const key = msg.id || msg.sender || msg.recipient;
              if (key) {
                existingMessagesMap.set(key, msg);
              }
            });

            // Merge new messages with existing ones
            latestMessages.forEach(newMsg => {
              const key = newMsg.id || newMsg.sender || newMsg.recipient;
              if (key) {
                const existingMsg = existingMessagesMap.get(key);
                if (existingMsg) {
                  // Update existing message if new one is more recent
                  const existingDate = new Date(existingMsg.created_time || existingMsg.created_at);
                  const newDate = new Date(newMsg.created_time || newMsg.created_at);
                  if (newDate > existingDate) {
                    existingMessagesMap.set(key, newMsg);
                  }
                } else {
                  // Add new message
                  existingMessagesMap.set(key, newMsg);
                }
              }
            });

            // Convert back to array and sort
            const result = Array.from(existingMessagesMap.values()).sort((a, b) => {
              const dateA = parseDate(a.created_time || a.created_at);
              const dateB = parseDate(b.created_time || b.created_at);
              return dateB - dateA; // Sort in descending order
            });

            return result;
          }));

          // Start polling if not already started
          const state = getState().metaBusinessSuite;
          if (!state.pollingInterval) {
            startPolling();
          }

          resolve({
            firebaseChats, unsubscribe: () => {
              // Clean up both the Firestore listener and the polling interval
              unsubscribe();
              const state = getState().metaBusinessSuite;
              if (state.pollingInterval) {
                clearInterval(state.pollingInterval);
                dispatch(setPollingInterval(null));
              }
            }
          });
        });
      });
    } catch (error) {
      console.error("Error fetching WhatsApp latest messages:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Listen to any new document inside whatsApp/*/messages for the selected business phone
export const listenToAllWhatsappMessages = createAsyncThunk(
  "metaBusinessSuite/listenToAllWhatsappMessages",
  async ({ selectedPhone }, { dispatch, getState }) => {
    if (!selectedPhone) return;

    // Use consistent chat ID resolution for WhatsApp
    const chatType = 'whatsapp';
    const businessNumber = getChatIdentifier({ sender_phone_number: selectedPhone.display_phone_number }, chatType);

    if (!businessNumber) {
      console.error('listenToAllWhatsappMessages: Unable to determine business number');
      return;
    }

    console.log('listenToAllWhatsappMessages - Using consistent chat ID resolution:', {
      businessNumber,
      originalPhone: selectedPhone.display_phone_number
    });

    // Clean previous listener if any
    const prevUnsub = getState().metaBusinessSuite.latestMessagesUnsubscribe;
    if (prevUnsub) {
      try { prevUnsub(); } catch (e) { /* noop */ }
    }

    // Use optimized WhatsApp collection group query
    console.log('[QUERY_OPTIMIZATION] Setting up optimized WhatsApp collection group listener for business:', businessNumber);

    const unsubscribe = listenToWhatsAppMessagesOptimized(businessNumber, (messages) => {
      const startTime = performance.now();

      // Group messages by customer phone for efficient processing
      const messagesByCustomer = new Map();

      messages.forEach((message) => {
        const customerPhone = message.chatId;
        if (!messagesByCustomer.has(customerPhone)) {
          messagesByCustomer.set(customerPhone, []);
        }
        messagesByCustomer.get(customerPhone).push(message);
      });

      // Process each customer's messages
      messagesByCustomer.forEach((customerMessages, customerPhone) => {
        const latestMessage = customerMessages.sort((a, b) =>
          new Date(b.created_time) - new Date(a.created_time)
        )[0];

        if (latestMessage) {
          // 1) merge into latestMessages
          dispatch(updateLatestMessages((prev = []) => {
            const rest = (prev || []).filter(
              (m) => !(m.sender === customerPhone || m.recipient === customerPhone)
            );
            return [
              {
                id: customerPhone,
                sender: latestMessage.sender,
                recipient: latestMessage.recipient,
                message: latestMessage.message,
                message_id: latestMessage.message_id,
                created_time: latestMessage.created_time,
                type: latestMessage.type || "text",
                url: latestMessage.url || null,
              },
              ...rest,
            ];
          }));

          // 2) update whatsappChats preview list
          dispatch(updateWhatsappChatsWithFirebase((prevChats = []) => {
            const others = prevChats.filter((c) => c.sender_phone_number !== customerPhone);
            const existing = prevChats.find((c) => c.sender_phone_number === customerPhone);
            const base = existing || {
              sender_phone_number: customerPhone,
              sender_name: latestMessage.sender_name || customerPhone,
            };

            return [
              {
                ...base,
                last_message: {
                  message: latestMessage.message,
                  from: latestMessage.sender,
                  type: latestMessage.type || "text",
                  message_id: latestMessage.message_id,
                  timestamp: Math.floor(new Date(latestMessage.created_time).getTime() / 1000).toString(),
                  created_at: latestMessage.created_time,
                  updated_at: latestMessage.created_time,
                  url: latestMessage.url || null
                },
                updated_time: latestMessage.created_time,
                created_time: latestMessage.created_time,
                created_at: latestMessage.created_time,
                updated_at: latestMessage.created_time,
                fromFirebase: true
              },
              ...others,
            ];
          }));
        }
      });

      const processingTime = Math.round((performance.now() - startTime) * 100) / 100;
      console.log('[QUERY_PERFORMANCE] WhatsApp collection group processed:', {
        totalMessages: messages.length,
        uniqueCustomers: messagesByCustomer.size,
        processingTime
      });
    }, { limit: 100, orderDirection: 'desc' });

    // Store the unsubscribe function
    dispatch(setLatestMessagesUnsubscribe(unsubscribe));

    return { unsubscribe };

  }
);

      // Select chat async thunk with Firebase cleanup
export const selectChatAsync = createAsyncThunk(
  "metaBusinessSuite/selectChatAsync",
  async ({ thread }, { dispatch, getState }) => {

    try {
      // Reset disabled chat state when switching to a new chat
      dispatch(setDisabledChat(false));

      // Clean up previous listener
      const state = getState().metaBusinessSuite;
      if (state.messagesSnapshotUnsubscribe) {
        state.messagesSnapshotUnsubscribe();
      }

      // Clean up polling interval
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval);
        dispatch(setPollingInterval(null));
      }

      // Store current unread count before clearing comments for preservation
      const previousUnreadCount = state.commentsModal.unreadCount;
      const previousChatId = state.selectedChat?.id;

      // Clean up any existing messages and comments for the selected chat
      if (thread && thread.id && state.selectedPage?.page_id) {

        try {
          const chatType = determineChatType(thread);

          // Import the messages clearing function only (keep comments for persistence)
          const { clearFirebaseMessages } = await import('../../services/comments/hybridService');

          // Clear only messages, not comments
          await clearFirebaseMessages(thread, chatType, state.selectedPage.page_id);
        } catch (error) {
          console.error('❌ [CHAT_SELECTION] Error cleaning up Firebase messages:', error);
        }
      } else {
        console.log('⚠️ [CHAT_SELECTION] Skipping Firebase cleanup - missing data:', {
          hasThread: !!thread,
          hasThreadId: !!thread?.id,
          hasPageId: !!state.selectedPage?.page_id,
          thread: thread,
          selectedPage: state.selectedPage
        });
      }

      // Fetch messages for the selected chat
      await dispatch(fetchMessages({ thread }));

      // Load comments for the selected chat using consistent chat ID resolution
      const chatType = determineChatType(thread);
      const firebaseChatId = getChatIdentifier(thread, chatType);

      await dispatch(loadChatCommentsHybrid({
        backendChatId: thread.id,
        firebaseChatId: firebaseChatId, // Use consistent chat ID resolution (backend chat ID, not participant ID)
        chatType: chatType,
        selectedChat: thread
      }));

      // Add a small delay to ensure Firebase listener is fully set up
      await new Promise(resolve => setTimeout(resolve, 100));
 
      const startRealtimeCommentsResult = await dispatch(startRealtimeCommentsListener({
        chatId: firebaseChatId, // Use the same firebaseChatId that was used for loading comments
        chatType: chatType,
        pageId: state.selectedPage?.page_id,
        autoMarkAsRead: false, // Don't auto-mark as read when selecting chat, only when opening modal
        autoScrollToBottom: true,
      }));

      if (startRealtimeCommentsResult.type.endsWith('/fulfilled')) {
        console.log('[CHAT_SELECTION_DEBUG] Real-time comments listener started successfully');
      } else {
        console.warn('[CHAT_SELECTION_DEBUG] Failed to start real-time comments listener:', startRealtimeCommentsResult.payload);
      }

      // Handle unread count preservation and recalculation
      const currentChatId = thread?.id;
      if (currentChatId) {
        console.log('[CHAT_SELECTION_DEBUG] Chat selection completed:', {
          currentChatId,
          previousChatId,
          previousUnreadCount,
          willRecalculateUnread: true
        });

        // Store the previous unread count in chat-specific storage for restoration
        if (previousChatId && previousUnreadCount > 0) {
          console.log('[CHAT_SELECTION_DEBUG] Preserving unread count for previous chat:', {
            previousChatId,
            previousUnreadCount
          });
          dispatch(updateGlobalUnreadComments({
            chatId: previousChatId,
            comments: [], // Empty array since we're preserving the count
            currentUserId: null // Will be set by the component
          }));
        }

        // Check if we're returning to a previously viewed chat
        const isReturningToChat = previousChatId === currentChatId;
        if (isReturningToChat) {
          console.log('[CHAT_SELECTION_DEBUG] Returning to same chat - will recalculate unread count');
          // The unread count will be recalculated by the Firebase listener
          // This ensures that if new comments were added while away, they will be counted
        } else {
          console.log('[CHAT_SELECTION_DEBUG] Switching to different chat - preserving previous chat unread count');
        }

        // The unread count will be recalculated by the Firebase listener
        // This ensures accurate counts when switching between chats or returning to chats
        console.log('[CHAT_SELECTION_DEBUG] Unread count will be recalculated by real-time listener');

        // TEST: Log current state for debugging
        console.log('[CHAT_SELECTION_TEST] Current state after chat selection:', {
          selectedChatId: currentChatId,
          commentsModalUnreadCount: state.commentsModal.unreadCount,
          chatUnreadComments: state.chatUnreadComments[currentChatId],
          globalUnreadCount: state.globalUnreadCount,
          commentsCount: state.comments.length
        });
      }

      console.log('[CHAT_SELECTION_DEBUG] Chat selected successfully');

      return thread;
    } catch (error) {
      console.error("Error selecting chat:", error);
      throw error;
    }
  }
);

// Select WhatsApp chat async thunk with Firebase cleanup
export const selectWhatsappChatAsync = createAsyncThunk(
  "metaBusinessSuite/selectWhatsappChatAsync",
  async ({ thread, selectedPhone }, { dispatch, getState }) => {
    try {
      // Reset disabled chat state when switching to a new chat
      dispatch(setDisabledChat(false));

      // Clean up previous listener
      const state = getState().metaBusinessSuite;
      if (state.messagesSnapshotUnsubscribe) {
        state.messagesSnapshotUnsubscribe();
      }

      // Clean up polling interval
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval);
        dispatch(setPollingInterval(null));
      }

      // Clean up Firebase messages and comments for the selected WhatsApp chat
      if (thread && thread.sender_phone_number) {
        console.log('🧹 [WHATSAPP_SELECTION] Cleaning up Firebase messages and comments for WhatsApp chat:', {
          senderPhone: thread.sender_phone_number,
          pageId: state.selectedPage?.page_id,
          thread: thread
        });

        try {
          // Import the messages clearing function only (keep comments for persistence)
          const { clearFirebaseMessages } = await import('../../services/comments/hybridService');

          // Clear only messages, not comments
          console.log('🧹 [WHATSAPP_SELECTION] About to call clearFirebaseMessages...');
          await clearFirebaseMessages(thread, 'whatsapp', state.selectedPage?.page_id);
          console.log('🧹 [WHATSAPP_SELECTION] Firebase messages cleanup completed successfully');
        } catch (error) {
          console.error('❌ [WHATSAPP_SELECTION] Error cleaning up Firebase messages for WhatsApp:', error);
        }
      } else {
        console.log('⚠️ [WHATSAPP_SELECTION] Skipping Firebase cleanup - missing data:', {
          hasThread: !!thread,
          hasSenderPhone: !!thread?.sender_phone_number,
          hasPageId: !!state.selectedPage?.page_id,
          thread: thread,
          selectedPage: state.selectedPage
        });
      }

      // Ensure the thread has the correct ID for fetching messages
      const threadWithId = {
        ...thread,
        id: thread.originalApiId || thread.id // Use originalApiId if available, otherwise use id
      };

      // Fetch WhatsApp messages for the selected chat
      await dispatch(fetchWhatsAppMessages({ thread: threadWithId, selectedPhone }));

      // Load comments for the selected WhatsApp chat
      await dispatch(loadChatCommentsHybrid({
        backendChatId: thread.id || thread.sender_phone_number,
        firebaseChatId: normalizePhoneNumber(thread.sender_phone_number),
        chatType: "whatsapp",
        selectedChat: thread
      }));

      // Start the comments real-time listener immediately when WhatsApp chat is selected
      // This ensures real-time updates begin as soon as chat is opened, not when comments modal is opened
      const firebaseChatId = normalizePhoneNumber(thread.sender_phone_number);
      console.log('[WHATSAPP_SELECTION_DEBUG] Starting real-time comments listener for WhatsApp chat:', firebaseChatId);

      const startRealtimeCommentsResult = await dispatch(startRealtimeCommentsListener({
        chatId: firebaseChatId,
        chatType: "whatsapp",
        pageId: state.selectedPage?.page_id,
        autoMarkAsRead: false, // Don't auto-mark as read when selecting chat, only when opening modal
        autoScrollToBottom: true,
      }));

      if (startRealtimeCommentsResult.type.endsWith('/fulfilled')) {
        console.log('[WHATSAPP_SELECTION_DEBUG] Real-time comments listener started successfully for WhatsApp chat');
      } else {
        console.warn('[WHATSAPP_SELECTION_DEBUG] Failed to start real-time comments listener for WhatsApp chat:', startRealtimeCommentsResult.payload);
      }

      // Don't reset unread count here - let the Firebase listener handle it properly
      // The pending state already handles clearing data for different chats
      const currentChatId = thread?.sender_phone_number || thread?.id;
      if (currentChatId) {
        console.log('[WHATSAPP_SELECTION_DEBUG] Preserving unread count for WhatsApp chat - will be recalculated by Firebase listener');
      }

      console.log('[WHATSAPP_SELECTION_DEBUG] WhatsApp chat selected, unread count will be recalculated by real-time listener');

      return thread;
    } catch (error) {
      console.error("Error selecting WhatsApp chat:", error);
      throw error;
    }
  }
);

// Async thunk to update Firebase parent document for incoming messages
export const updateFirebaseParentDocument = createAsyncThunk(
  "metaBusinessSuite/updateFirebaseParentDocument",
  async ({ selectedChat, latestMessage }, { getState, rejectWithValue }) => {
    try {
      if (!selectedChat || !latestMessage) {
        return { success: false, reason: 'Missing required parameters' };
      }

      // Get selectedPage from Redux state
      const state = getState().metaBusinessSuite;
      const selectedPage = state.selectedPage;

      // Determine chat type and get simplified collection paths
      const chatType = determineChatType(selectedChat);

      if (!selectedPage?.page_id) {
        return { success: false, reason: 'No page selected' };
      }

      // Use simplified path structure: ${pageId}/${sender}/messages
      const collectionPaths = getCollectionPaths(selectedChat, chatType, selectedPage.page_id);

      if (!collectionPaths.messages) {
        return { success: false, reason: 'Could not determine collection paths' };
      }

      // Prepare message data for simplified structure
      const messageData = {
        id: latestMessage.id || `incoming_${Date.now()}`,
        message: latestMessage.message,
        sender: latestMessage.sender || latestMessage.from?.id,
        recipient: latestMessage.recipient,
        created_time: latestMessage.created_time || new Date().toISOString(),
        type: latestMessage.type || 'text'
      };

      console.log('[INCOMING_MESSAGE] Writing message to simplified path:', {
        messagesPath: collectionPaths.messages,
        pageId: selectedPage?.page_id,
        chatType,
        messageData
      });

      // Write the message to the simplified path: ${pageId}/${sender}/messages
      const messageRef = doc(db, ...collectionPaths.messages.split('/'), messageData.id);
      await setDoc(messageRef, messageData);

      console.log('[INCOMING_MESSAGE] Firebase message written successfully to simplified path');

      return {
        success: true,
        messagesPath: collectionPaths.messages,
        messageData
      };

    } catch (error) {
      console.error('[INCOMING_MESSAGE] Error updating Firebase parent document:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to handle incoming messages with Firebase parent document update
export const handleIncomingMessage = createAsyncThunk(
  "metaBusinessSuite/handleIncomingMessage",
  async ({ messages, selectedChat }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!messages || !Array.isArray(messages) || messages.length === 0) {
        return { success: false, reason: 'No messages provided' };
      }

      // First, update the messages in Redux state
      dispatch(updateMessages(messages));

      // Get the latest message for parent document update
      const latestMessage = messages[messages.length - 1];

      if (!selectedChat) {
        // If no selectedChat provided, try to get it from state
        const state = getState().metaBusinessSuite;
        selectedChat = state.selectedChat;
      }

      if (!selectedChat) {
        console.warn('[INCOMING_MESSAGE] No selected chat available for parent document update');
        return { success: true, reason: 'Messages updated but no parent document update' };
      }

      // Update the Firebase parent document
      const result = await dispatch(updateFirebaseParentDocument({
        selectedChat,
        latestMessage
      }));

      if (result.type.endsWith('/fulfilled')) {
        console.log('[INCOMING_MESSAGE] Successfully handled incoming message');
        return {
          success: true,
          messagesCount: messages.length,
          parentDocumentUpdated: true
        };
      } else {
        console.warn('[INCOMING_MESSAGE] Parent document update failed:', result.payload);
        return {
          success: true,
          messagesCount: messages.length,
          parentDocumentUpdated: false,
          reason: result.payload
        };
      }

    } catch (error) {
      console.error('[INCOMING_MESSAGE] Error handling incoming message:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const sendMessage = createAsyncThunk(
  "metaBusinessSuite/sendMessage",
  async ({ data, selectedChat }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await metaService.sendMessageApi(data);
      // Check for 24-hour window error in the response
      if (response && typeof response === 'object') {
        // Check if response has an error property
        if (response.error) {
          // Check for 24-hour window error (code 10)
          if (response.error.code === 10 ||
            response.error.error_subcode === 2018278 ||
            (response.error.message && response.error.message.includes("(#10)")) ||
            (response.error.message && response.error.message.toLowerCase().includes("outside the allowed window"))) {
            dispatch(setDisabledChat(true));
            return false; // Return false to indicate error
          }

          // Handle other API errors
          return false; // Return false to indicate error
        }
      }

      const currentDate = new Date();
      const formattedDate = currentDate.toISOString();

      // Ensure we have a valid selectedChat before proceeding
      if (!selectedChat || !selectedChat.participants || !selectedChat.participants.data) {
        console.error("Invalid selectedChat object:", selectedChat);
        return false;
      }

      // Safely extract recipient and sender IDs with fallbacks
      let recipient, sender;

      try {
        recipient = selectedChat?.flage === "instagram"
          ? (selectedChat.participants.data[1]?.id || "").toString()
          : (selectedChat.participants.data[0]?.id || "").toString();

        sender = selectedChat?.flage === "instagram"
          ? (selectedChat.participants.data[0]?.id || "").toString()
          : (selectedChat.participants.data[1]?.id || "").toString();
      } catch (error) {
        console.error("Error extracting recipient/sender IDs:", error);
        return false;
      }

      // Validate recipient ID
      if (!recipient) {
        console.error("Invalid recipient ID");
        return false;
      }

      // Determine message type based on data
      const messageType = data.get("type") ||
        (response?.file_url ?
          (response.file_url.match(/\.(jpeg|jpg|gif|png)$/) ? "image" :
            response.file_url.match(/\.(mp4|mov|wmv|avi)$/) ? "video" : "file")
          : "text");

      const newMessage = {
        id: response?.message_id || `msg_${Date.now()}`,
        type: messageType,
        sender: sender,
        recipient: recipient,
        message: response?.file_url ? response?.file_url : data.get("message"),
        created_time: formattedDate,
      };

      // --- Firestore updates with dual write support ---

      try {
        // Determine chat type and get simplified collection paths
        const chatType = determineChatType(selectedChat);

        // Get selectedPage from Redux state
        const state = getState().metaBusinessSuite;
        const selectedPage = state.selectedPage;

        if (!selectedPage?.page_id) {
          console.error('SendMessage: No page selected, cannot determine collection paths');
          return rejectWithValue('No page selected');
        }

        // Use simplified path structure: ${pageId}/${sender}/messages
        const collectionPaths = getCollectionPaths(selectedChat, chatType, selectedPage.page_id);

        console.log('SendMessage - Using simplified collection paths:', {
          chatType,
          messagesPath: collectionPaths.messages,
          selectedChatId: selectedChat?.id,
          pageId: selectedPage?.page_id,
          sender,
          recipient
        });

        console.log(`[SEND_MESSAGE_DEBUG] *** MESSAGE STORAGE PATH ***`);
        console.log(`[SEND_MESSAGE_DEBUG] Storing message at: ${collectionPaths.messages}/${newMessage.id}`);
        console.log(`[SEND_MESSAGE_DEBUG] Full path breakdown: pageId=${selectedPage?.page_id}, senderId=${getSenderId(selectedChat, chatType)}, messageId=${newMessage.id}`);

        if (!collectionPaths.messages) {
          console.error('SendMessage: Could not determine collection paths');
          return rejectWithValue('Could not determine collection paths');
        }

        // Write message to simplified path: ${pageId}/${sender}/messages
        try {
          const messageRef = doc(db, ...collectionPaths.messages.split('/'), newMessage.id);
          await setDoc(messageRef, newMessage);

          console.log('SendMessage - Message write successful:', `${collectionPaths.messages}/${newMessage.id}`);

          // Immediately add the message to Redux state for instant UI update
          dispatch(updateMessages([newMessage]));

        } catch (error) {
          console.error('SendMessage - Message write failed:', error);
          throw error;
        }

        // Update the parent chat document with latest message info
        try {
          const senderId = getSenderId(selectedChat, chatType);
          const chatDocPath = `pages/${selectedPage.page_id}/chats/${senderId}`;
          const chatDocRef = doc(db, ...chatDocPath.split('/'));

          console.log(`[SEND_MESSAGE_DEBUG] Updating parent chat document at: ${chatDocPath}`);
          console.log(`[SEND_MESSAGE_DEBUG] Setting updated_time to: ${newMessage.created_time}`);

          await updateDoc(chatDocRef, {
            message: newMessage.message,
            type: newMessage.type,
            updated_time: newMessage.created_time,
            last_message_id: newMessage.id,
            last_message_sender: newMessage.sender,
            last_message_time: newMessage.created_time
          });

          console.log('[SEND_MESSAGE_DEBUG] Parent chat document updated successfully');
        } catch (error) {
          console.error('SendMessage - Parent chat document update failed:', error);
          // Don't throw here as the message was already sent successfully
        }

        // Immediately update latestMessages state so chat list shows the sent message
        // This provides instant feedback while waiting for the real-time listener
        try {
          const senderId = getSenderId(selectedChat, chatType);
          console.log(`[SEND_MESSAGE_DEBUG] Immediately updating latestMessages for senderId: ${senderId}`);

          dispatch(updateLatestMessages((prevLatestMessages) => {
            if (!prevLatestMessages || !Array.isArray(prevLatestMessages)) {
              return prevLatestMessages;
            }

            const updatedMessages = prevLatestMessages.map(chat => {
              if (chat.id === senderId) {
                console.log(`[SEND_MESSAGE_DEBUG] Found matching chat in latestMessages, updating message`);
                return {
                  ...chat,
                  message: newMessage.message,
                  type: newMessage.type,
                  updated_time: newMessage.created_time,
                  last_message_id: newMessage.id,
                  last_message_sender: newMessage.sender,
                  last_message_time: newMessage.created_time
                };
              }
              return chat;
            });

            // If no existing chat found, add a new entry
            const existingChat = updatedMessages.find(chat => chat.id === senderId);
            if (!existingChat) {
              console.log(`[SEND_MESSAGE_DEBUG] No existing chat found in latestMessages, adding new entry`);
              updatedMessages.push({
                id: senderId,
                message: newMessage.message,
                type: newMessage.type,
                updated_time: newMessage.created_time,
                created_time: newMessage.created_time,
                last_message_id: newMessage.id,
                last_message_sender: newMessage.sender,
                last_message_time: newMessage.created_time,
                sender_name: selectedChat?.participants?.data?.[chatType === 'instagram' ? 1 : 0]?.name || 'Unknown'
              });
            }

            return updatedMessages;
          }));
        } catch (error) {
          console.error('SendMessage - Immediate latestMessages update failed:', error);
        }

        // Update the appropriate chat list based on activeFilter
        const updateChatList = (prevChats) => {
          if (!prevChats) return prevChats;

          const updatedChats = prevChats.map(chat => {
            if (chat.id === selectedChat.id) {
              return {
                ...chat,
                updated_time: formattedDate,
                message: newMessage.message,
                type: messageType
              };
            }
            return chat;
          });

          return updatedChats.sort((a, b) =>
            parseDate(b.updated_time) - parseDate(a.updated_time)
          );
        };

        if (selectedChat?.flage === "instagram") {
          dispatch(updateInstagramChats(updateChatList));
        } else {
          dispatch(updateMessengerChats(updateChatList));
        }

        return true; // Return true to indicate success

      } catch (error) {
        console.error("Error updating Firebase:", error);

        // Log Firebase write failure
        logDualWriteOperation('sendMessage_firebase_error', false, error, {
          chatType: determineChatType(selectedChat),
          selectedChatId: selectedChat?.id,
          recipient,
          sender
        });

        return false;
      }
    } catch (error) {
      console.error("Error in sendMessage:", error);

      // Check for 24-hour window error in the caught error
      if (error && error.response && error.response.data && error.response.data.error) {
        const apiError = error.response.data.error;
        if (apiError.code === 10 ||
          apiError.error_subcode === 2018278 ||
          (apiError.message && apiError.message.includes("(#10)")) ||
          (apiError.message && apiError.message.toLowerCase().includes("outside the allowed window"))) {
          dispatch(setDisabledChat(true));
          return false;
        }
      }

      return false;
    }
  }
);

export const sendWhatsMessage = createAsyncThunk(
  "metaBusinessSuite/sendWhatsMessage",
  async ({ data, selectedPhone, selectedWhatsAccount, selectedChat, selectedWhatsappChat }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await metaService.sendWhatsMessageApi(data);

      const fileUrl = response?.file_url || null;

      if (response?.error) {
        console.error("Error in WhatsApp API response:", response.error);
        return false;
      }

      // Create a new message object
      const currentDate = new Date();
      const formattedDate = currentDate.toISOString();

      // Business phone number (from the selected phone in context)
      const senderPhone = normalizePhoneNumber(selectedPhone?.display_phone_number) || '';

      // Determine customer phone number from either selectedWhatsappChat or selectedChat
      const customerPhoneNumberRaw = selectedWhatsappChat?.sender_phone_number || selectedChat?.sender_phone_number || '';
      const recipientPhone = normalizePhoneNumber(customerPhoneNumberRaw);

      if (!recipientPhone) {
        throw new Error('Invalid recipient phone number');
      }

      console.log("SendWhatsMessage - Customer phone processing:", {
        raw: customerPhoneNumberRaw,
        processed: recipientPhone,
        selectedWhatsappChat: selectedWhatsappChat?.sender_phone_number,
        selectedChat: selectedChat?.sender_phone_number
      });

      console.log("SendWhatsMessage - Will save message to Firebase path:", `whatsApp/${recipientPhone}/messages`);

      const senderName = selectedWhatsAccount?.name || "Business";
      const messageText = data.get("text") || "";
      const messageType = data.get("type") || "text";

      // For media messages (image, video, audio, sticker), store the URL in both message and url fields
      const isMediaMessage = messageType === "image" ||
        messageType === "video" ||
        messageType === "audio" ||
        messageType === "sticker";

      // Define senderId and recipientId for use in the function
      const senderId = senderPhone;
      const recipientId = recipientPhone;

      // Create the message object with the URL field for media messages
      const newMessage = {
        id: response?.messages?.[0]?.id || response?.message_id || `whats_${Date.now()}`,
        message_id: response?.messages?.[0]?.id || response?.message_id || `whats_${Date.now()}`,
        type: messageType,
        sender: senderPhone,
        sender_name: senderName,
        recipient: recipientPhone,
        message: isMediaMessage ? fileUrl : messageText,
        // Add URL field for media messages
        url: isMediaMessage ? fileUrl : null,
        created_time: formattedDate,
        // Add MIME type for audio messages
        mime_type: messageType === "audio" ? "audio/mp3" : null
      };

      try {
        // Update the selectedWhatsappChat with the new timestamp
        dispatch(updateSelectedWhatsappChat((prevChat) => {
          if (!prevChat) return prevChat;
          const updatedChat = {
            ...prevChat,
            updated_at: formattedDate
          };

          return updatedChat;
        }));

        // Use simplified path structure for WhatsApp messages
        const chatType = 'whatsapp';

        // Get selectedPage from Redux state
        const state = getState().metaBusinessSuite;
        const selectedPage = state.selectedPage;

        if (!selectedPage?.page_id) {
          throw new Error('No page selected for WhatsApp message');
        }

        // Use simplified path structure: ${pageId}/${sender}/messages
        const collectionPaths = getCollectionPaths(selectedWhatsappChat || selectedChat, chatType, selectedPage.page_id);

        console.log('SendWhatsMessage - Using simplified collection paths:', {
          chatType,
          messagesPath: collectionPaths.messages,
          pageId: selectedPage.page_id,
          recipientPhone
        });

        if (!collectionPaths.messages) {
          throw new Error('Could not determine collection paths for WhatsApp message');
        }

        // Write message to simplified path: ${pageId}/${sender}/messages
        try {
          const messageRef = doc(db, ...collectionPaths.messages.split('/'), newMessage.id);
          await setDoc(messageRef, newMessage);

          console.log("SendWhatsMessage - Write successful:", `${collectionPaths.messages}/${newMessage.id}`);
        } catch (error) {
          console.error('SendWhatsMessage - Message write failed:', error);
          throw error;
        }

        // Update the parent chat document with latest message info
        try {
          const senderId = getSenderId(selectedWhatsappChat || selectedChat, chatType);
          const chatDocPath = `pages/${selectedPage.page_id}/chats/${senderId}`;
          const chatDocRef = doc(db, ...chatDocPath.split('/'));

          console.log(`[SEND_WHATS_MESSAGE_DEBUG] Updating parent chat document at: ${chatDocPath}`);
          console.log(`[SEND_WHATS_MESSAGE_DEBUG] Setting updated_time to: ${newMessage.created_time}`);

          await updateDoc(chatDocRef, {
            message: newMessage.message,
            type: newMessage.type,
            updated_time: newMessage.created_time,
            last_message_id: newMessage.id,
            last_message_sender: newMessage.sender,
            last_message_time: newMessage.created_time
          });

          console.log('[SEND_WHATS_MESSAGE_DEBUG] Parent chat document updated successfully');
        } catch (error) {
          console.error('SendWhatsMessage - Parent chat document update failed:', error);
          // Don't throw here as the message was already sent successfully
        }

        // Immediately update latestMessages state so chat list shows the sent message
        // This provides instant feedback while waiting for the real-time listener
        try {
          const senderId = getSenderId(selectedWhatsappChat || selectedChat, chatType);
          console.log(`[SEND_WHATS_MESSAGE_DEBUG] Immediately updating latestMessages for senderId: ${senderId}`);

          dispatch(updateLatestMessages((prevLatestMessages) => {
            if (!prevLatestMessages || !Array.isArray(prevLatestMessages)) {
              return prevLatestMessages;
            }

            const updatedMessages = prevLatestMessages.map(chat => {
              if (chat.id === senderId) {
                console.log(`[SEND_WHATS_MESSAGE_DEBUG] Found matching chat in latestMessages, updating message`);
                return {
                  ...chat,
                  message: newMessage.message,
                  type: newMessage.type,
                  updated_time: newMessage.created_time,
                  last_message_id: newMessage.id,
                  last_message_sender: newMessage.sender,
                  last_message_time: newMessage.created_time
                };
              }
              return chat;
            });

            // If no existing chat found, add a new entry
            const existingChat = updatedMessages.find(chat => chat.id === senderId);
            if (!existingChat) {
              console.log(`[SEND_WHATS_MESSAGE_DEBUG] No existing chat found in latestMessages, adding new entry`);
              updatedMessages.push({
                id: senderId,
                message: newMessage.message,
                type: newMessage.type,
                updated_time: newMessage.created_time,
                created_time: newMessage.created_time,
                last_message_id: newMessage.id,
                last_message_sender: newMessage.sender,
                last_message_time: newMessage.created_time,
                sender_name: senderName,
                sender_phone_number: senderPhone
              });
            }

            return updatedMessages;
          }));
        } catch (error) {
          console.error('SendWhatsMessage - Immediate latestMessages update failed:', error);
        }



        // Immediately merge the sent message into the open chat thread
        console.log("About to add message to chat immediately:", newMessage);
        dispatch(updateWhatsappChatMessages([newMessage]));

        console.log("Sent message added to chat immediately:", newMessage);

        // Verify the message was added to state
        const currentState = getState().metaBusinessSuite;
        console.log("Current whatsappChatMessages count after adding:", currentState.whatsappChatMessages.length);
        console.log("Last message in chat:", currentState.whatsappChatMessages[currentState.whatsappChatMessages.length - 1]);

        // Additional debug logging to verify Firebase listener path matches storage path
        const { selectedWhatsappChat: currentSelectedChat } = getState().metaBusinessSuite;
        if (currentSelectedChat) {
          const listenerPhoneId = normalizePhoneNumber(currentSelectedChat.sender_phone_number);
          console.log("Firebase listener should be active for path:", `whatsApp/${listenerPhoneId}/messages`);
          console.log("Message stored at path:", `whatsApp/${recipientPhone}/messages`);
          console.log("Paths match:", listenerPhoneId === recipientPhone);

          if (listenerPhoneId !== recipientPhone) {
            console.error("PHONE NUMBER MISMATCH - Firebase listener won't pick up the message!");
            console.error("Listener phone:", listenerPhoneId);
            console.error("Storage phone:", recipientPhone);
            console.error("Selected chat:", currentSelectedChat);
          }
        }

        // Force a small delay to ensure Firebase write is complete before listener picks it up
        setTimeout(() => {
          console.log("Firebase write should be complete, listener should have triggered");
        }, 100);

        console.log("Message saved to Firebase:", {
          collection: `whatsApp/${recipientPhone}/messages`,
          messageId: newMessage.id,
          message: newMessage
        });

        // Note: Latest messages will be updated automatically by the real-time chats listener
        // when it detects the Firebase parent document update above

        // Update WhatsApp chats list - FIXED: Ensure proper chat matching and sorting
        dispatch(updateWhatsappChatsWithFirebase((prevChats) => {
          if (!prevChats) return prevChats;

          const updatedChats = prevChats.map(chat => {
            // Match by sender_phone_number to ensure we update the correct chat
            if (chat.sender_phone_number === selectedWhatsappChat.sender_phone_number) {
              const updatedChat = {
                ...chat,
                // Preserve the original numeric id from the API
                id: chat.id, // This should be the numeric ID from the API
                updated_at: formattedDate,
                last_message: {
                  ...chat.last_message,
                  message: isMediaMessage ? fileUrl : messageText,
                  from: senderPhone,
                  type: messageType,
                  // Add URL field for media messages
                  url: isMediaMessage ? fileUrl : null,
                  message_id: newMessage.message_id,
                  timestamp: Math.floor(currentDate.getTime() / 1000).toString(),
                  created_at: formattedDate,
                  updated_at: formattedDate
                }
              };

              return updatedChat;
            }
            return chat;
          });

          // Sort by latest message time
          const sortedChats = updatedChats.sort((a, b) => {
            const timeA = a.last_message?.created_at
              ? parseDate(a.last_message.created_at)
              : a.updated_at
                ? parseDate(a.updated_at)
                : parseDate(a.created_at);

            const timeB = b.last_message?.created_at
              ? parseDate(b.last_message.created_at)
              : b.updated_at
                ? parseDate(b.updated_at)
                : parseDate(b.created_at);

            return timeB - timeA; // Sort in descending order (newest first)
          });

          return sortedChats;
        }));

        return true; // Return true to indicate success

      } catch (error) {
        console.error("Error sending WhatsApp message:", error);
        return false;
      }
    } catch (error) {
      console.error("Error in sendWhatsMessage:", error);

      // Log WhatsApp message send failure
      logDualWriteOperation('sendWhatsMessage_error', false, error, {
        chatType: 'whatsapp',
        selectedPhone: selectedPhone?.id,
        selectedWhatsappChat: selectedWhatsappChat?.sender_phone_number,
        messageType: data.get("type") || "text"
      });

      return rejectWithValue(error?.message || "Failed to send WhatsApp message");
    }
  }
);

// Fetch WhatsApp chats from API and sync with Firebase
export const fetchWhatsAppChatsFromAPI = createAsyncThunk(
  "metaBusinessSuite/fetchWhatsAppChatsFromAPI",
  async ({ selectedPhone }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!selectedPhone) {
        return [];
      }

      // Fetch chats from API
      const response = await metaService.getWhatsAppChatsForPageApi({
        id: selectedPhone.id,
      });

      if (!response?.data) {
        return [];
      }

      const apiChats = response.data;

      // Sync each API chat with Firebase
      for (const apiChat of apiChats) {
        try {
          const chatDocRef = doc(db, "whatsApp", apiChat.sender_phone_number);

          const docData = {
            sender: apiChat.last_message?.from === selectedPhone?.id ?
              selectedPhone?.display_phone_number.trim().replace(/^\+|\s+/g, '') :
              apiChat.sender_phone_number,
            sender_name: apiChat.sender_name,
            recipient: apiChat.last_message?.from === selectedPhone?.id ?
              apiChat.sender_phone_number :
              selectedPhone?.display_phone_number.trim().replace(/^\+|\s+/g, ''),
            message: apiChat.last_message?.message || "",
            message_id: apiChat.last_message?.message_id || apiChat.last_message?.id,
            created_time: apiChat.last_message?.created_at || apiChat.created_at,
            type: apiChat.last_message?.type || "text",
            url: apiChat.last_message?.url || null,
            originalApiId: apiChat.id // Store the API ID
          };

          await setDoc(chatDocRef, docData);
        } catch (error) {
          console.error('Error syncing API chat to Firebase:', apiChat.sender_phone_number, error);
        }
      }

      return apiChats;
    } catch (error) {
      console.error("Error fetching WhatsApp chats from API:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Comments system async thunks
export const fetchComments = createAsyncThunk(
  "metaBusinessSuite/fetchComments",
  async ({ chatId, chatType }, { getState, rejectWithValue }) => {
    try {
      const { fetchComments: fetchCommentsService } = await import('../../services/comments');
      const { validateChatContext } = await import('../../utils/commentValidation');
      const { handleCommentError } = await import('../../utils/commentErrorHandling');

      // Get current user for permission validation
      const state = getState();
      const currentUser = state.auth?.user;
      const selectedPage = state.metaBusinessSuite?.selectedPage;

      // Validate chat context before making the request
      const chatValidation = validateChatContext(chatId, chatType);
      if (!chatValidation.isValid) {
        return rejectWithValue(`Invalid chat context: ${chatValidation.errors.join(', ')}`);
      }

      // Ensure pageId is a string
      const pageId = selectedPage?.page_id ? String(selectedPage.page_id) : null;

      const comments = await fetchCommentsService(chatId, chatType, currentUser, pageId);
      return comments;
    } catch (error) {
      console.error("Error fetching comments:", error);

      // Use centralized error handling
      const { handleCommentError } = await import('../../utils/commentErrorHandling');
      const commentError = handleCommentError(error, 'fetch comments', {
        showToast: false, // Don't show toast, let UI handle it
        throwError: false
      });

      return rejectWithValue(commentError.message);
    }
  }
);

// Hybrid Comments Actions
export const loadChatCommentsHybrid = createAsyncThunk(
  "metaBusinessSuite/loadChatCommentsHybrid",
  async ({ backendChatId, firebaseChatId, chatType, selectedChat }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { loadChatComments } = await import('../../services/comments/hybridService');

      // Get selectedPage from Redux state
      const state = getState().metaBusinessSuite;
      const selectedPage = state.selectedPage;

      const result = await loadChatComments(
        backendChatId,
        firebaseChatId,
        chatType,
        (comments) => {
          console.log('[HYBRID_COMMENTS_DEBUG] onCommentsUpdate callback fired with:', comments.length, 'comments');

          // Get current user ID
          const state = getState();
          const currentUser = state.auth?.user;
          const currentUserId = currentUser?.user?.id ? String(currentUser.user.id) : null;

          console.log('[HYBRID_COMMENTS_DEBUG] Current user ID:', currentUserId);
          console.log('[HYBRID_COMMENTS_DEBUG] Comments details:', comments.map(c => ({ id: c.id, author: c.author?.id, text: c.text?.substring(0, 30) })));

          // Update comments in real-time via dispatch
          dispatch(updateCommentsRealtime({ comments, currentUserId }));

          // Calculate unread count
          if (currentUser) {
            const { getUnreadComments } = require('../../utils/chatUtils');
            const unreadComments = getUnreadComments(comments, String(currentUser.user.id));
            console.log('[HYBRID_COMMENTS_DEBUG] Calculated unread comments:', unreadComments.length);
            dispatch(setCommentsUnreadCount(unreadComments.length));
          }
        },
        selectedPage?.page_id,
        selectedChat
      );

      // Store the unsubscribe function
      dispatch(setCommentsListener(result.unsubscribe));

      return result.comments;
    } catch (error) {
      console.error("Error loading chat comments:", error);
      return rejectWithValue(error.message || 'Failed to load comments');
    }
  }
);

export const sendCommentHybrid = createAsyncThunk(
  "metaBusinessSuite/sendCommentHybrid",
  async ({ backendChatId, firebaseChatId, chatType, text, author }, { getState, rejectWithValue }) => {
    try {
      const { sendComment } = await import('../../services/comments/hybridService');
      const { validateCommentText, validateAuthor } = await import('../../utils/commentValidation');

      // Get selectedPage from Redux state
      const state = getState().metaBusinessSuite;
      const selectedPage = state.selectedPage;

      // Validate inputs
      const textValidation = validateCommentText(text);
      if (!textValidation.isValid) {
        return rejectWithValue(`Invalid comment: ${textValidation.errors.join(', ')}`);
      }

      const authorValidation = validateAuthor(author);
      if (!authorValidation.isValid) {
        return rejectWithValue(`Invalid author: ${authorValidation.errors.join(', ')}`);
      }

      // Ensure pageId is a string
      const pageId = selectedPage?.page_id ? String(selectedPage.page_id) : null;

      const comment = await sendComment(backendChatId, firebaseChatId, chatType, text, author, pageId);
      return comment;
    } catch (error) {
      console.error("Error sending comment:", error);
      
      // Enhanced error handling with more specific messages
      let errorMessage = 'Failed to send comment';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.status) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid comment data. Please check your input.';
            break;
          case 401:
            errorMessage = 'You are not authorized to send comments. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to send comments in this chat.';
            break;
          case 404:
            errorMessage = 'Chat not found. Please refresh and try again.';
            break;
          case 422:
            errorMessage = 'Comment validation failed. Please check your input.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Request failed with status ${error.response.status}`;
        }
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const markCommentsAsReadHybrid = createAsyncThunk(
  "metaBusinessSuite/markCommentsAsReadHybrid",
  async ({ backendChatId, firebaseChatId, chatType, commentIds, user }, { getState, rejectWithValue }) => {
    try {
      console.log('🔄 [MARK_COMMENTS_READ_HYBRID] Starting with:', {
        backendChatId,
        firebaseChatId,
        chatType,
        commentIds: commentIds?.length || 0,
        user: user?.name || user?.id
      });

      // Get selectedPage from Redux state
      const state = getState().metaBusinessSuite;
      const selectedPage = state.selectedPage;

      console.log('🔄 [MARK_COMMENTS_READ_HYBRID] Selected page:', selectedPage?.page_id);

      const { markCommentsAsRead } = await import('../../services/comments/hybridService');

      console.log('🔄 [MARK_COMMENTS_READ_HYBRID] About to call markCommentsAsRead service');

      await markCommentsAsRead(backendChatId, firebaseChatId, chatType, commentIds, user, selectedPage?.page_id);

      console.log('✅ [MARK_COMMENTS_READ_HYBRID] Successfully completed');
      return { commentIds, user };
    } catch (error) {
      console.error("❌ [MARK_COMMENTS_READ_HYBRID] Error:", error);
      return rejectWithValue(error.message || 'Failed to mark comments as read');
    }
  }
);

export const addComment = createAsyncThunk(
  "metaBusinessSuite/addComment",
  async ({ chatId, chatType, text, author }, { getState, rejectWithValue }) => {
    try {
      const { addComment: addCommentService } = await import('../../services/comments');
      const { validateCommentText, validateChatContext, validateAuthor } = await import('../../utils/commentValidation');

      // Get current user for permission validation
      const state = getState();
      const currentUser = state.auth?.user;
      const selectedPage = state.metaBusinessSuite?.selectedPage;

      // Validate inputs before making the request
      const chatValidation = validateChatContext(chatId, chatType);
      if (!chatValidation.isValid) {
        return rejectWithValue(`Invalid chat context: ${chatValidation.errors.join(', ')}`);
      }

      const textValidation = validateCommentText(text);
      if (!textValidation.isValid) {
        return rejectWithValue(`Invalid comment: ${textValidation.errors.join(', ')}`);
      }

      const authorValidation = validateAuthor(author);
      if (!authorValidation.isValid) {
        return rejectWithValue(`Invalid author: ${authorValidation.errors.join(', ')}`);
      }

      // Ensure pageId is a string
      const pageId = selectedPage?.page_id ? String(selectedPage.page_id) : null;

      const commentId = await addCommentService(chatId, chatType, text, author, currentUser, null, pageId);

      // Return the comment data that will be received via real-time listener
      return {
        id: commentId,
        text: textValidation.sanitizedText,
        author: authorValidation.sanitizedAuthor,
        createdAt: new Date().toISOString(),
        readBy: [],
        chatType
      };
    } catch (error) {
      console.error("Error adding comment:", error);

      // Use centralized error handling
      const { handleCommentError } = await import('../../utils/commentErrorHandling');
      const commentError = handleCommentError(error, 'add comment', {
        showToast: true, // Show toast for add comment errors
        throwError: false
      });

      return rejectWithValue(commentError.message);
    }
  }
);

export const listenToComments = createAsyncThunk(
  "metaBusinessSuite/listenToComments",
  async ({ chatId, chatType, currentUserId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { listenToComments: listenToCommentsService } = await import('../../services/comments');
      const { validateChatContext } = await import('../../utils/commentValidation');

      // Get current user for permission validation
      const state = getState();
      const currentUser = state.auth?.user;
      const selectedPage = state.metaBusinessSuite?.selectedPage;

      // Validate chat context before setting up listener
      const chatValidation = validateChatContext(chatId, chatType);
      if (!chatValidation.isValid) {
        return rejectWithValue(`Invalid chat context: ${chatValidation.errors.join(', ')}`);
      }

      // Clean up any existing listener first
      const metaState = getState().metaBusinessSuite;
      if (metaState.commentsListeners.unsubscribe) {
        metaState.commentsListeners.unsubscribe();
      }

      // Ensure pageId is a string
      const pageId = selectedPage?.page_id ? String(selectedPage.page_id) : null;

      const unsubscribe = listenToCommentsService(chatId, chatType, (comments, error) => {
        if (error) {
          console.error("Comments listener error:", error);
          dispatch(setCommentsLoading(false));

          // Use centralized error handling for listener errors
          import('../../utils/commentErrorHandling').then(({ handleCommentError }) => {
            handleCommentError(error, 'comments listener', {
              showToast: true,
              throwError: false
            });
          });
          return;
        }

        // Update comments in real-time
        dispatch(updateCommentsRealtime({ comments, currentUserId }));

        // Calculate unread count for current user
        if (currentUserId && comments.length > 0) {
          const unreadCount = comments.filter(comment => {
            const hasRead = comment.readBy?.some(receipt => receipt.userId === currentUserId);
            return !hasRead;
          }).length;

          dispatch(setCommentsUnreadCount(unreadCount));
        }

        dispatch(setCommentsLoading(false));
      }, currentUser, pageId);

      return unsubscribe;
    } catch (error) {
      console.error("Error setting up comments listener:", error);

      // Use centralized error handling
      const { handleCommentError } = await import('../../utils/commentErrorHandling');
      const commentError = handleCommentError(error, 'setup comments listener', {
        showToast: true,
        throwError: false
      });

      return rejectWithValue(commentError.message);
    }
  }
);

export const markCommentsAsReadAsync = createAsyncThunk(
  "metaBusinessSuite/markCommentsAsReadAsync",
  async ({ chatId, chatType, user, commentIds }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { markCommentsAsRead } = await import('../../services/comments');
      const { validateChatContext } = await import('../../utils/commentValidation');

      // Get current user for permission validation
      const state = getState();
      const currentUser = state.auth?.user;
      const selectedPage = state.metaBusinessSuite?.selectedPage;

      // Validate chat context
      const chatValidation = validateChatContext(chatId, chatType);
      if (!chatValidation.isValid) {
        return rejectWithValue(`Invalid chat context: ${chatValidation.errors.join(', ')}`);
      }

      // Validate user information
      if (!user || !user.id || !user.name) {
        return rejectWithValue('Valid user information is required');
      }

      // Validate comment IDs
      if (!Array.isArray(commentIds) || commentIds.length === 0) {
        return rejectWithValue('Valid comment IDs are required');
      }

      // Ensure pageId is a string
      const pageId = selectedPage?.page_id ? String(selectedPage.page_id) : null;

      await markCommentsAsRead(chatId, chatType, commentIds, user, currentUser, pageId);

      // Update local state immediately after successful Firebase update
      dispatch(markCommentsAsRead({ userId: user.id, userName: user.name }));

      return { userId: user.id, userName: user.name };
    } catch (error) {
      console.error("Error marking comments as read:", error);

      // Use centralized error handling
      const { handleCommentError } = await import('../../utils/commentErrorHandling');
      const commentError = handleCommentError(error, 'mark comments as read', {
        showToast: true,
        throwError: false
      });

      return rejectWithValue(commentError.message);
    }
  }
);

// Start comments listener with automatic cleanup
export const startCommentsListener = createAsyncThunk(
  "metaBusinessSuite/startCommentsListener",
  async ({ chatId, chatType, currentUserId }, { dispatch, getState }) => {
    try {
      // Clean up any existing listener first
      dispatch(clearCommentsListener());

      // Start the new listener
      await dispatch(listenToComments({ chatId, chatType, currentUserId }));

      return { chatId, chatType };
    } catch (error) {
      console.error("Error starting comments listener:", error);
      throw error;
    }
  }
);

// Stop comments listener and cleanup
export const stopCommentsListener = createAsyncThunk(
  "metaBusinessSuite/stopCommentsListener",
  async (_, { dispatch }) => {
    try {
      dispatch(clearCommentsListener());
      dispatch(setComments([]));
      dispatch(setCommentsUnreadCount(0));
      return true;
    } catch (error) {
      console.error("Error stopping comments listener:", error);
      throw error;
    }
  }
);

// Enhanced Real-time Listeners using RealtimeManager
export const startRealtimeChatsListener = createAsyncThunk(
  "metaBusinessSuite/startRealtimeChatsListener",
  async ({ pageId }, { dispatch, getState }) => {
    try {
      const realtimeManager = (await import('../../services/firebase/realtimeManager')).default;

      // Clean up any existing listeners
      realtimeManager.cleanup(`all_chats_${pageId}`);

      const unsubscribe = realtimeManager.listenToAllChats(
        pageId,
        (chats, error) => {
          if (error) {
            console.error("Real-time chats listener error:", error);
            return;
          }

          // Update chats in Redux state - replace entire latestMessages array with fresh Firebase data
          console.log(`[REALTIME_CHATS_DEBUG] Received ${chats.length} chats from Firebase, dispatching to Redux`);
          console.log(`[REALTIME_CHATS_DEBUG] Sample chat data:`, chats.slice(0, 2).map(chat => ({
            id: chat.id,
            message: chat.message?.substring(0, 30),
            updated_time: chat.updated_time
          })));

          // Sort chats by updated_time before setting in Redux (newest first)
          const sortedChats = [...chats].sort((a, b) => {
            const timeA = new Date(a.updated_time || a.created_time || 0);
            const timeB = new Date(b.updated_time || b.created_time || 0);
            return timeB - timeA; // Descending order (newest first)
          });

          console.log(`[REALTIME_CHATS_DEBUG] Sorted ${sortedChats.length} chats by updated_time`);
          console.log(`[REALTIME_CHATS_DEBUG] Top 3 sorted chats:`, sortedChats.slice(0, 3).map(chat => ({
            id: chat.id,
            message: chat.message?.substring(0, 30),
            updated_time: chat.updated_time
          })));

          dispatch(setLatestMessages(sortedChats));
        },
        {
          enablePerformanceMonitoring: true,
          batchUpdates: true
        }
      );

      // Store unsubscribe function
      dispatch(setLatestMessagesUnsubscribe(unsubscribe));

      return { pageId, listenerCount: realtimeManager.getActiveListenerCount() };
    } catch (error) {
      console.error("Error starting real-time chats listener:", error);
      throw error;
    }
  }
);

export const startRealtimeMessagesListener = createAsyncThunk(
  "metaBusinessSuite/startRealtimeMessagesListener",
  async ({ chatId, chatType, pageId }, { dispatch, getState }) => {
    try {
      const realtimeManager = (await import('../../services/firebase/realtimeManager')).default;

      // Clean up any existing listeners for this chat
      realtimeManager.cleanup(`messages_${pageId}_${chatId}`);

      const unsubscribe = realtimeManager.listenToMessages(
        chatId,
        chatType,
        pageId,
        (messages, error) => {
          if (error) {
            console.error("Real-time messages listener error:", error);
            return;
          }

          console.log(`[REDUX_DEBUG] Received ${messages.length} messages for ${chatType} chat ${chatId}`);
          console.log(`[REDUX_DEBUG] Messages:`, messages);

          // Update messages in Redux state based on chat type
          if (chatType === 'whatsapp') {
            console.log(`[REDUX_DEBUG] Dispatching updateWhatsappChatMessages`);
            dispatch(updateWhatsappChatMessages(messages));
          } else {
            console.log(`[REDUX_DEBUG] Dispatching updateMessages`);
            dispatch(updateMessages(messages));
          }
        },
        {
          enablePerformanceMonitoring: true,
          autoScrollToBottom: true
        }
      );

      // Store unsubscribe function
      dispatch(setMessagesSnapshotUnsubscribe(unsubscribe));

      return { chatId, chatType, pageId };
    } catch (error) {
      console.error("Error starting real-time messages listener:", error);
      throw error;
    }
  }
);

export const startRealtimeCommentsListener = createAsyncThunk(
  "metaBusinessSuite/startRealtimeCommentsListener",
  async ({ chatId, chatType, pageId, autoMarkAsRead = true, autoScrollToBottom = false }, { dispatch, getState }) => {
    try {
      const realtimeManager = (await import('../../services/firebase/realtimeManager')).default;
      const state = getState();
      const currentUser = state.auth?.user;

      if (!currentUser) {
        throw new Error("User must be authenticated to listen to comments");
      }

      // Clean up any existing listeners for this sender
      realtimeManager.cleanup(`comments_${pageId}_${chatId}`);

      // Use chatId as senderId for Firebase path (chatId here is actually the sender ID from getChatIds)
      const unsubscribe = realtimeManager.listenToComments(
        chatId, // This is actually the sender ID from getChatIds
        chatType,
        pageId,
        (comments, error) => {
          if (error) {
            console.error("Real-time comments listener error:", error);
            dispatch(setCommentsLoading(false));
            return;
          }

          // Update comments in Redux state (this will calculate unread count automatically)
          const currentUserId = currentUser?.user?.id ? String(currentUser.user.id) : (currentUser?.id ? String(currentUser.id) : null);
          dispatch(updateCommentsRealtime({ comments, currentUserId }));

          // Note: Unread count is calculated inside updateCommentsRealtime
          // Don't override it here to preserve count when reselecting same chat

          dispatch(setCommentsLoading(false));
        },
        {
          id: String(currentUser.user?.id || currentUser.id),
          name: currentUser.user?.name || currentUser.name,
          photo: currentUser.user?.photo || currentUser.photo
        },
        {
          autoMarkAsRead,
          autoScrollToBottom,
          enablePerformanceMonitoring: true,
          batchReadReceipts: true
        }
      );

      // Store unsubscribe function
      dispatch(setCommentsListener(unsubscribe));

      return { chatId, chatType, pageId, autoMarkAsRead, autoScrollToBottom };
    } catch (error) {
      console.error("Error starting real-time comments listener:", error);
      throw error;
    }
  }
);

export const stopAllRealtimeListeners = createAsyncThunk(
  "metaBusinessSuite/stopAllRealtimeListeners",
  async (_, { dispatch }) => {
    try {
      const realtimeManager = (await import('../../services/firebase/realtimeManager')).default;

      // Get status before cleanup
      const status = realtimeManager.getStatus();
      console.log('[REALTIME] Stopping all listeners:', status);

      // Clean up all listeners
      realtimeManager.cleanupAll();

      // Clear Redux state
      dispatch(clearCommentsListener());
      dispatch(setLatestMessagesUnsubscribe(null));
      dispatch(setMessagesSnapshotUnsubscribe(null));

      return { cleanedUp: status.totalListeners };
    } catch (error) {
      console.error("Error stopping all real-time listeners:", error);
      throw error;
    }
  }
);

export const markCommentsAsReadRealtime = createAsyncThunk(
  "metaBusinessSuite/markCommentsAsReadRealtime",
  async ({ chatId, chatType, pageId, commentIds }, { getState }) => {
    try {
      const realtimeManager = (await import('../../services/firebase/realtimeManager')).default;
      const state = getState();
      const currentUser = state.auth?.user;

      if (!currentUser) {
        throw new Error("User must be authenticated to mark comments as read");
      }

      const userInfo = {
        id: String(currentUser.user?.id || currentUser.id),
        name: currentUser.user?.name || currentUser.name,
        photo: currentUser.user?.photo || currentUser.photo
      };

      await realtimeManager.markCommentsAsReadInFirebase(
        chatId,
        chatType,
        pageId,
        commentIds,
        userInfo
      );

      return { chatId, commentIds, user: userInfo };
    } catch (error) {
      console.error("Error marking comments as read in real-time:", error);
      throw error;
    }
  }
);

export const getRealtimeListenerStatus = createAsyncThunk(
  "metaBusinessSuite/getRealtimeListenerStatus",
  async () => {
    try {
      const realtimeManager = (await import('../../services/firebase/realtimeManager')).default;
      return realtimeManager.getStatus();
    } catch (error) {
      console.error("Error getting real-time listener status:", error);
      return { error: error.message };
    }
  }
);

// Initial state
const initialState = {
  // Chat state
  chats: [],
  selectedChat: null,
  messages: [],
  selectedChatStatus: null,
  nestedMessages: [],
  latestMessages: null,

  // Page and filter state
  selectedPage: null,
  activeFilter: "messenger",
  messengerChats: [],
  instagramChats: [],

  // WhatsApp state
  whatsappChats: [],
  whatsappChatMessages: [],
  whatsAppAccounts: [],
  selectedWhatsAccount: null,
  selectedWhatsappChat: null,
  whatsAppAccountDetails: [],
  selectedPhone: null,
  businessPhoneNumbers: [],

  // Filter and pagination state
  startDate: "",
  endDate: "",
  hasMore: true,
  loadingChats: false,
  loading: {
    fetchingLatestMessages: false,
    fetchingMessages: false,
    sendingMessage: false,
    sendingWhatsMessage: false,
    fetchingWhatsAppChats: false,
  },
  // Global unread comments tracking
  globalUnreadComments: {},  // { chatId: { count: number, comments: [], lastUpdated: string } }
  globalUnreadCount: 0,      // Total unread comments across all chats
  shouldPulseCommentsButton: false, // Trigger pulse effect for new comments in other chats
  
  // Messages pagination state
  messagesPagination: {
    olderMessages: [],
    nextUrl: null,
    hasMore: false,
    isLoading: false,
  },
  
  // UI state
  disabledChat: false,
  disabledChatLimit: false,
  loadingPagination: false,
  // Loading states for async operations
  fetchingMoreChats: false,
  fetchingOlderMessages: false,
  // Error states
  error: {
    fetchLatestMessages: null,
    fetchMessages: null,
    fetchWhatsAppMessages: null,
    sendMessage: null,
    sendWhatsMessage: null,
    fetchMoreChats: null,
    fetchChats: null,
  },

  // Firestore listeners
  messagesSnapshotUnsubscribe: null,
  latestMessagesUnsubscribe: null,
  pollingInterval: null,

  // Comments system state
  comments: [],
  commentsModal: {
    isOpen: false,
    unreadCount: 0,
    loading: false,
    initialLoading: false, // Separate state for initial loading vs comment submission
  },
  commentsListeners: {
    unsubscribe: null,
  },
  // Track unread comments per chat
  chatUnreadComments: {}, // { chatId: { count: number, lastReadAt: timestamp } }
  // Track unread messages per chat
  chatUnreadMessages: {}, // { chatId: { count: number, lastReadAt: timestamp } }
  shouldPlayNotificationSound: false,
  lastNotificationTimestamp: 0, // Prevent notification spam
  shouldRefreshComments: false,

  // Failed messages state for retry mechanism
  failedMessages: new Map(), // { messageId: { messageData, error, timestamp } }
};

// Helper function to determine chat platform type
const determineChatPlatform = (chat) => {
  // Enhanced chat type detection logic
  const isInstagram =
    // Direct platform indicators
    chat.platform === 'instagram' ||
    chat.source === 'instagram' ||
    chat.flage === 'instagram' ||
    // Participant-based detection (Instagram users have username but no email)
    (chat.participants?.data?.[0]?.username && !chat.participants?.data?.[0]?.email) ||
    // URL-based detection
    chat.link?.includes('instagram.com') ||
    // Message source detection
    chat.message_source === 'instagram' ||
    // ID pattern detection (Instagram IDs are typically longer)
    (chat.id && chat.id.length > 15 && !chat.id.includes('_'));

  return isInstagram ? 'instagram' : 'messenger';
};

// Create slice
const metaBusinessSuiteSlice = createSlice({
  name: "metaBusinessSuite",
  initialState,
  reducers: {
    // Basic setters
    setChats: (state, action) => {
      state.chats = action.payload;
    },
    setSelectedChat: (state, action) => {
      state.selectedChat = action.payload;
      // Reset pulse effect when selecting a chat
      state.shouldPulseCommentsButton = false;
      // Clear comments when switching chats
      if (state.selectedChat !== action.payload) {
        state.comments = [];
        state.commentsModal.unreadCount = 0;
        state.commentsModal.isOpen = false;
        // Clear any existing listener
        if (state.commentsListeners.unsubscribe) {
          state.commentsListeners.unsubscribe();
          state.commentsListeners.unsubscribe = null;
        }
      }
    },
    setMessages: (state, action) => {
      state.messages = action.payload;
      // Reset pagination when new messages are loaded
      state.messagesPagination.olderMessages = [];
      state.messagesPagination.nextUrl = null;
      state.messagesPagination.hasMore = false;
    },
    setSelectedChatStatus: (state, action) => {
      state.selectedChatStatus = action.payload;
    },
    setNestedMessages: (state, action) => {
      state.nestedMessages = action.payload;
    },
    // Pagination management
    setMessagesPaginationUrl: (state, action) => {
      state.messagesPagination.nextUrl = action.payload;
      state.messagesPagination.hasMore = !!action.payload;
    },
    resetMessagesPagination: (state) => {
      state.messagesPagination.olderMessages = [];
      state.messagesPagination.nextUrl = null;
      state.messagesPagination.hasMore = false;
      state.messagesPagination.isLoading = false;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    setLatestMessages: (state, action) => {
      const newLatestMessages = action.payload;
      state.latestMessages = newLatestMessages;

      // Auto-add new chats to appropriate arrays if they don't exist
      if (Array.isArray(newLatestMessages) && newLatestMessages.length > 0) {
        const existingMessengerIds = new Set(state.messengerChats.map(chat => chat.id));
        const existingInstagramIds = new Set(state.instagramChats.map(chat => chat.id));

        const newMessengerChats = [];
        const newInstagramChats = [];

        newLatestMessages.forEach(chat => {
          const platform = determineChatPlatform(chat);

          if (platform === 'instagram' && !existingInstagramIds.has(chat.id)) {
            newInstagramChats.push(chat);
            existingInstagramIds.add(chat.id);
          } else if (platform === 'messenger' && !existingMessengerIds.has(chat.id)) {
            newMessengerChats.push(chat);
            existingMessengerIds.add(chat.id);
          }
        });

        // Add new chats to the appropriate arrays
        if (newMessengerChats.length > 0) {
          console.log(`[AUTO_ADD_CHATS] Adding ${newMessengerChats.length} new Messenger chats:`, newMessengerChats.map(c => c.id));
          state.messengerChats = [...state.messengerChats, ...newMessengerChats];
        }

        if (newInstagramChats.length > 0) {
          console.log(`[AUTO_ADD_CHATS] Adding ${newInstagramChats.length} new Instagram chats:`, newInstagramChats.map(c => c.id));
          state.instagramChats = [...state.instagramChats, ...newInstagramChats];
        }
      }
    },
    setActiveFilter: (state, action) => {
      // Clear comments when switching between chat types (WhatsApp vs Messenger/Instagram)
      if (state.activeFilter !== action.payload) {
        state.comments = [];
        state.commentsModal.unreadCount = 0;
        state.commentsModal.isOpen = false;
        // Clear any existing listener
        if (state.commentsListeners.unsubscribe) {
          state.commentsListeners.unsubscribe();
          state.commentsListeners.unsubscribe = null;
        }
      }
      state.activeFilter = action.payload;
    },
    setMessengerChats: (state, action) => {
      state.messengerChats = action.payload;
    },
    setInstagramChats: (state, action) => {
      state.instagramChats = action.payload;
    },
    setWhatsappChats: (state, action) => {
      state.whatsappChats = action.payload;
    },
    setWhatsappChatMessages: (state, action) => {
      state.whatsappChatMessages = action.payload;
    },
    setWhatsAppAccounts: (state, action) => {
      state.whatsAppAccounts = action.payload;
    },
    setSelectedWhatsAccount: (state, action) => {
      state.selectedWhatsAccount = action.payload;
    },
    setSelectedWhatsappChat: (state, action) => {
      state.selectedWhatsappChat = action.payload;
      // Reset pulse effect when selecting a WhatsApp chat
      state.shouldPulseCommentsButton = false;
      // Clear comments when switching chats
      if (state.selectedWhatsappChat !== action.payload) {
        state.comments = [];
        state.commentsModal.unreadCount = 0;
        state.commentsModal.isOpen = false;
        // Clear any existing listener
        if (state.commentsListeners.unsubscribe) {
          state.commentsListeners.unsubscribe();
          state.commentsListeners.unsubscribe = null;
        }
      }
    },
    setWhatsAppAccountDetails: (state, action) => {
      state.whatsAppAccountDetails = action.payload;
    },
    setSelectedPhone: (state, action) => {
      state.selectedPhone = action.payload;
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },
    setFilteredData: (state, action) => {
      state.filteredData = action.payload;
    },
    setConversations: (state, action) => {
      state.conversations = action.payload;
    },
    setBusinessPhoneNumbers: (state, action) => {
      state.businessPhoneNumbers = action.payload;
    },
    setDisabledChat: (state, action) => {
      state.disabledChat = action.payload;
    },
    setDisabledChatLimit: (state, action) => {
      state.disabledChatLimit = action.payload;
    },
    setPageImg: (state, action) => {
      state.pageImg = action.payload;
    },
    setPaginationMeta: (state, action) => {
      state.paginationMeta = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setLoadingPagination: (state, action) => {
      state.loadingPagination = action.payload;
    },
    setLoadingChats: (state, action) => {
      state.loadingChats = action.payload;
    },
    setMessagesSnapshotUnsubscribe: (state, action) => {
      state.messagesSnapshotUnsubscribe = action.payload;
    },
    setLatestMessagesUnsubscribe: (state, action) => {
      state.latestMessagesUnsubscribe = action.payload;
    },
    setPollingInterval: (state, action) => {
      state.pollingInterval = action.payload;
    },
    selectChat: (state, action) => {
      const thread = action.payload;
      state.selectedChat = thread;
      state.messages = [];
      state.nestedMessages = [];
      state.disabledChat = false;

      // Clean up previous listener
      if (state.messagesSnapshotUnsubscribe) {
        state.messagesSnapshotUnsubscribe();
        state.nestedMessages = [];
        state.messagesSnapshotUnsubscribe = null;
      }
    },
    selectWhatsappChat: (state, action) => {
      const thread = action.payload;
      state.selectedChat = thread;
      state.whatsappChatMessages = [];
      state.nestedMessages = [];
      state.disabledChat = false;
      state.selectedWhatsappChat = thread;

      // Clean up previous listener
      if (state.messagesSnapshotUnsubscribe) {
        state.messagesSnapshotUnsubscribe();
        state.nestedMessages = [];
        state.messagesSnapshotUnsubscribe = null;
      }
    },
    updateWhatsappChatsWithFirebase: (state, action) => {
      state.whatsappChats = action.payload(state.whatsappChats);
    },
    handleSelectWhatsAppAccount: (state, action) => {
      const { whatsAppAccount, phone } = action.payload;
      state.selectedWhatsAccount = whatsAppAccount;
      state.selectedPhone = phone;
      state.businessPhoneNumbers = whatsAppAccount?.phone_numbers?.data?.map((account) => ({
        ...account,
        phone: parsePhoneNumber(account?.display_phone_number),
      })) || [];
    },
    resetFilteredChatsPages: (state) => {
      state.nestedMessages = [];
      state.messages = [];
    },
    addNewChatsFromFirebase: (state, action) => {
      // Explicitly add new chats detected from Firebase listeners
      const { messengerChats = [], instagramChats = [] } = action.payload;

      if (messengerChats.length > 0) {
        const existingIds = new Set(state.messengerChats.map(chat => chat.id));
        const newChats = messengerChats.filter(chat => !existingIds.has(chat.id));
        if (newChats.length > 0) {
          console.log(`[FIREBASE_NEW_CHATS] Adding ${newChats.length} new Messenger chats from Firebase`);
          state.messengerChats = [...state.messengerChats, ...newChats];
        }
      }

      if (instagramChats.length > 0) {
        const existingIds = new Set(state.instagramChats.map(chat => chat.id));
        const newChats = instagramChats.filter(chat => !existingIds.has(chat.id));
        if (newChats.length > 0) {
          console.log(`[FIREBASE_NEW_CHATS] Adding ${newChats.length} new Instagram chats from Firebase`);
          state.instagramChats = [...state.instagramChats, ...newChats];
        }
      }
    },
    // Debug function to help diagnose message flow issues
    debugMessageFlow: (state, action) => {
      const { selectedChat, latestMessages, messages } = state;
      const debugInfo = {
        timestamp: new Date().toISOString(),
        selectedChat: selectedChat ? {
          id: selectedChat.id,
          type: selectedChat.sender_phone_number ? 'whatsapp' :
            selectedChat.flage === 'instagram' ? 'instagram' : 'messenger',
          participants: selectedChat.participants?.data?.map(p => p.id)
        } : null,
        latestMessagesCount: latestMessages?.length || 0,
        messagesCount: messages?.length || 0,
        latestMessagesForChat: latestMessages?.filter(msg =>
          selectedChat && (
            msg.id === selectedChat.id ||
            msg.chat_id === selectedChat.id ||
            msg.sender === selectedChat.participants?.data?.[0]?.id ||
            msg.recipient === selectedChat.participants?.data?.[0]?.id
          )
        ) || [],
        action: action.payload
      };

      console.log('[DEBUG_MESSAGE_FLOW]', debugInfo);

      // Store debug info in state for inspection
      state.debugInfo = debugInfo;
    },
    updateMessages: (state, action) => {
      const newMessages = action.payload;
      console.log(`[REDUCER_DEBUG] updateMessages called with ${newMessages.length} messages`);
      console.log(`[REDUCER_DEBUG] Current messages count: ${state.messages.length}`);
      console.log(`[REDUCER_DEBUG] New messages:`, newMessages);

      const uniqueMessages = [...state.messages];
      let addedCount = 0;

      newMessages.forEach(message => {
        if (!uniqueMessages.some((m) => m.id === message.id)) {
          uniqueMessages.push(message);
          addedCount++;
          console.log(`[REDUCER_DEBUG] Added new message:`, message);
        } else {
          console.log(`[REDUCER_DEBUG] Message already exists:`, message.id);
        }
      });

      uniqueMessages.sort(
        (a, b) => parseDate(a.created_time) - parseDate(b.created_time),
      );
      state.messages = uniqueMessages;

      console.log(`[REDUCER_DEBUG] Final messages count: ${state.messages.length}, Added: ${addedCount}`);

      // For incoming messages, we need to update the Firebase parent document
      // so that the latest messages listener can pick up the changes
      if (state.selectedChat && newMessages.length > 0) {
        const latestMessage = newMessages[newMessages.length - 1]; // Get the most recent message

        // Use the new chat ID-based approach for consistency
        const chatType = state.selectedChat?.sender_phone_number ? 'whatsapp' :
          state.selectedChat?.flage === 'instagram' ? 'instagram' : 'messenger';

        let chatId;
        if (chatType === 'whatsapp') {
          chatId = state.selectedChat.sender_phone_number?.toString().trim().replace(/^\+|\s+|-/g, "");
        } else {
          chatId = state.selectedChat.id;
        }

        if (chatId && state.latestMessages) {
          // Update the latestMessages array using the consistent chat ID
          const updatedLatestMessages = state.latestMessages.map(msg => {
            // Check if this message belongs to the current chat using chat ID
            if (msg.id?.toString() === chatId?.toString() ||
              msg.sender?.toString() === chatId?.toString() ||
              msg.recipient?.toString() === chatId?.toString()) {
              return {
                ...msg,
                message: latestMessage.message,
                created_time: latestMessage.created_time,
                updated_time: latestMessage.created_time,
              };
            }
            return msg;
          });

          // If no existing message found, add a new one using chat ID
          const existingMessage = updatedLatestMessages.find(msg =>
            msg.id?.toString() === chatId?.toString() ||
            msg.sender?.toString() === chatId?.toString() ||
            msg.recipient?.toString() === chatId?.toString()
          );

          if (!existingMessage) {
            // Create new latest message entry using chat ID for consistency
            const participantIndex = state.selectedChat?.flage === 'instagram' ? 1 : 0;
            const participantId = state.selectedChat?.participants?.data[participantIndex]?.id;

            updatedLatestMessages.push({
              id: chatId, // Use chat ID as the identifier
              sender: latestMessage.sender || participantId,
              recipient: latestMessage.recipient || state.selectedChat?.participants?.data[participantIndex === 1 ? 0 : 1]?.id,
              message: latestMessage.message,
              created_time: latestMessage.created_time,
              updated_time: latestMessage.created_time,
              page_id: state.selectedPage?.page_id // Maintain page association
            });
          }

          state.latestMessages = updatedLatestMessages;
        }
      }
    },
    updateWhatsappChatMessages: (state, action) => {
      const newMessages = action.payload;
      console.log(`[WHATSAPP_REDUCER_DEBUG] updateWhatsappChatMessages called with:`, newMessages);
      console.log(`[WHATSAPP_REDUCER_DEBUG] Current WhatsApp messages count: ${state.whatsappChatMessages.length}`);

      // Ensure newMessages is an array
      if (!Array.isArray(newMessages)) {
        console.log(`[WHATSAPP_REDUCER_DEBUG] newMessages is not an array:`, typeof newMessages);
        return;
      }

      const uniqueMessages = [...state.whatsappChatMessages];
      let addedCount = 0;

      newMessages.forEach(newMsg => {
        const exists = uniqueMessages.some(msg =>
          msg.id === newMsg.id ||
          (msg.message_id && msg.message_id === newMsg.message_id) ||
          (msg.message === newMsg.message &&
            msg.sender === newMsg.sender &&
            msg.created_time === newMsg.created_time)
        );

        if (!exists) {
          console.log(`[WHATSAPP_REDUCER_DEBUG] Adding new message to chat:`, newMsg);
          uniqueMessages.push(newMsg);
          addedCount++;
        } else {
          console.log(`[WHATSAPP_REDUCER_DEBUG] Message already exists, skipping:`, newMsg.id);
        }
      });

      // Sort messages by creation time (ascending order for display)
      const sortedMessages = uniqueMessages.sort((a, b) => {
        const dateA = parseDate(a.created_time);
        const dateB = parseDate(b.created_time);
        return dateA - dateB;
      });

      state.whatsappChatMessages = sortedMessages;

      // Also update whatsappChats if we have a selected WhatsApp chat and new messages
      if (state.selectedWhatsappChat && newMessages.length > 0) {
        // Find the latest message from the new messages
        const latestMessage = newMessages.sort((a, b) => {
          const dateA = parseDate(a.created_time);
          const dateB = parseDate(b.created_time);
          return dateB - dateA; // Sort in descending order to get the latest
        })[0];

        // Update the selected WhatsApp chat with the latest message
        const updatedWhatsappChats = state.whatsappChats.map(chat => {
          // Match by sender_phone_number to ensure we update the correct chat
          if (chat.sender_phone_number === state.selectedWhatsappChat.sender_phone_number) {
            return {
              ...chat,
              last_message: {
                ...chat.last_message,
                message: latestMessage.message,
                from: latestMessage.sender,
                type: latestMessage.type || "text",
                message_id: latestMessage.message_id,
                timestamp: Math.floor(new Date(latestMessage.created_time).getTime() / 1000).toString(),
                created_at: latestMessage.created_time,
                updated_at: latestMessage.created_time,
                url: latestMessage.url || null
              },
              updated_time: latestMessage.created_time,
              created_time: latestMessage.created_time,
            };
          }
          return chat;
        });

        // Sort by latest message time
        const sortedChats = updatedWhatsappChats.sort((a, b) => {
          const timeA = a.last_message?.created_at
            ? parseDate(a.last_message.created_at)
            : a.updated_at
              ? parseDate(a.updated_at)
              : parseDate(a.created_at);

          const timeB = b.last_message?.created_at
            ? parseDate(b.last_message.created_at)
            : b.updated_at
              ? parseDate(b.updated_at)
              : parseDate(b.created_at);

          return timeB - timeA; // Sort in descending order (newest first)
        });

        state.whatsappChats = sortedChats;
      }

      // Also update latestMessages for the merged selector
      if (newMessages.length > 0 && state.selectedWhatsappChat) {
        // Find the latest message from the new messages
        const latestMessage = newMessages.sort((a, b) => {
          const dateA = parseDate(a.created_time);
          const dateB = parseDate(b.created_time);
          return dateB - dateA; // Sort in descending order to get the latest
        })[0];

        // Update latestMessages to include the latest WhatsApp message
        if (state.latestMessages) {
          const updatedLatestMessages = state.latestMessages.map(msg => {
            // Match by phone numbers - check if this message is for the current chat
            const msgSender = msg.sender?.toString();
            const msgRecipient = msg.recipient?.toString();
            const currentSender = state.selectedWhatsappChat.sender_phone_number?.toString();
            const currentRecipient = state.selectedPhone?.display_phone_number?.trim().replace(/^\+|\s+/g, '');

            if ((msgSender === currentSender && msgRecipient === currentRecipient) ||
              (msgSender === currentRecipient && msgRecipient === currentSender)) {
              return {
                ...msg,
                message: latestMessage.message,
                message_id: latestMessage.message_id,
                created_time: latestMessage.created_time,
                updated_time: latestMessage.created_time,
                created_at: latestMessage.created_time,
                url: latestMessage.url || null,
                type: latestMessage.type || "text"
              };
            }
            return msg;
          });

          // If no existing message was updated, add a new one
          const messageExists = updatedLatestMessages.some(msg => {
            const msgSender = msg.sender?.toString();
            const msgRecipient = msg.recipient?.toString();
            const currentSender = state.selectedWhatsappChat.sender_phone_number?.toString();
            const currentRecipient = state.selectedPhone?.display_phone_number?.trim().replace(/^\+|\s+/g, '');

            return (msgSender === currentSender && msgRecipient === currentRecipient) ||
              (msgSender === currentRecipient && msgRecipient === currentSender);
          });

          if (!messageExists) {
            updatedLatestMessages.push({
              id: state.selectedWhatsappChat.sender_phone_number,
              sender: latestMessage.sender,
              recipient: latestMessage.recipient,
              message: latestMessage.message,
              message_id: latestMessage.message_id,
              created_time: latestMessage.created_time,
              updated_time: latestMessage.created_time,
              created_at: latestMessage.created_time,
              type: latestMessage.type || "text",
              url: latestMessage.url || null
            });
          }

          state.latestMessages = updatedLatestMessages.sort((a, b) =>
            parseDate(b.created_time) - parseDate(a.created_time)
          );
        }
      }
    },
    clearErrors: (state) => {
      state.error = {
        fetchLatestMessages: null,
        fetchMessages: null,
        fetchWhatsAppMessages: null,
        sendMessage: null,
        sendWhatsMessage: null,
        fetchMoreChats: null,
        fetchChats: null,
      };
    },
    updateChats: (state, action) => {
      state.chats = action.payload(state.chats);
    },
    updateLatestMessages: (state, action) => {
      const newLatestMessages = action.payload(state.latestMessages);

      // Ensure proper sorting by updated_time (most recent first)
      if (Array.isArray(newLatestMessages)) {
        const sortedMessages = [...newLatestMessages].sort((a, b) => {
          const timeA = new Date(a.updated_time || a.created_time || 0);
          const timeB = new Date(b.updated_time || b.created_time || 0);
          return timeB - timeA; // Descending order (newest first)
        });

        console.log(`[LATEST_MESSAGES_DEBUG] Updated ${sortedMessages.length} chats, reordered by updated_time`);
        console.log(`[LATEST_MESSAGES_DEBUG] Top 3 chats:`, sortedMessages.slice(0, 3).map(chat => ({
          id: chat.id,
          sender_name: chat.sender_name,
          message: chat.message?.substring(0, 50),
          updated_time: chat.updated_time
        })));
        console.log(`[LATEST_MESSAGES_DEBUG] All chat IDs in latestMessages:`, sortedMessages.map(chat => chat.id));

        state.latestMessages = sortedMessages;
      } else {
        state.latestMessages = newLatestMessages;
      }
    },
    updateInstagramChats: (state, action) => {
      state.instagramChats = action.payload(state.instagramChats);
    },
    updateMessengerChats: (state, action) => {
      state.messengerChats = action.payload(state.messengerChats);
    },
    updateSelectedWhatsappChat: (state, action) => {
      state.selectedWhatsappChat = action.payload(state.selectedWhatsappChat);
    },

    // Comments system actions
    setComments: (state, action) => {
      state.comments = action.payload;
    },
    addCommentToState: (state, action) => {
      state.comments.push(action.payload);
    },
    updateComment: (state, action) => {
      const { commentId, updates } = action.payload;
      const commentIndex = state.comments.findIndex(comment => comment.id === commentId);
      if (commentIndex !== -1) {
        state.comments[commentIndex] = { ...state.comments[commentIndex], ...updates };
      }
    },
    removeComment: (state, action) => {
      state.comments = state.comments.filter(comment => comment.id !== action.payload);
    },
    setCommentsModal: (state, action) => {
      state.commentsModal = { ...state.commentsModal, ...action.payload };
    },
    openCommentsModal: (state) => {
      state.commentsModal.isOpen = true;

      // When opening the modal, mark all unread comments as read for the current user
      // Note: This will be handled by the component that dispatches this action
      // The component should dispatch markCommentsAsReadHybrid with the current user info
      console.log('🔄 [COMMENTS_MODAL] Modal opened - comments should be marked as read by component');
    },
    closeCommentsModal: (state) => {
      state.commentsModal.isOpen = false;
    },
    setCommentsUnreadCount: (state, action) => {
      state.commentsModal.unreadCount = action.payload;
    },
    incrementCommentsUnreadCount: (state) => {
      state.commentsModal.unreadCount += 1;
    },
    resetChatUnreadCount: (state, action) => {
      const { chatId } = action.payload;
      
      // Ensure chatUnreadComments is initialized
      if (!state.chatUnreadComments) {
        state.chatUnreadComments = {};
      }
      
      // Initialize or reset the specific chat's unread count
      state.chatUnreadComments[chatId] = {
        count: 0,
        lastReadAt: new Date().toISOString()
      };
    },
    setCommentsLoading: (state, action) => {
      state.commentsModal.loading = action.payload;
    },
    setCommentsInitialLoading: (state, action) => {
      state.commentsModal.initialLoading = action.payload;
    },
    setCommentsListener: (state, action) => {
      state.commentsListeners.unsubscribe = action.payload;
    },
    clearCommentsListener: (state) => {
      if (state.commentsListeners.unsubscribe) {
        state.commentsListeners.unsubscribe();
        state.commentsListeners.unsubscribe = null;
      }
    },
    updateCommentsRealtime: (state, action) => {
      const { comments: newComments, currentUserId } = action.payload;
      const oldUnreadCount = state.commentsModal.unreadCount;

      // Get current chat information for better context
      const currentChatId = state.selectedChat?.id || state.selectedWhatsappChat?.id;
      const previousComments = state.comments || [];
      const hasSignificantPreviousData = previousComments.length > 0;

      console.log('🔄 [COMMENTS_REALTIME] Processing update:', {
        newCommentsCount: newComments.length,
        previousCommentsCount: previousComments.length,
        currentChatId,
        oldUnreadCount,
        hasSignificantPreviousData
      });

      // SAFEGUARD 1: Handle empty comments array scenarios
      // Only ignore if we suspect this is a Firebase listener restart (not a legitimate chat switch)
      if (newComments.length === 0 && hasSignificantPreviousData && previousComments.length > 10) {
        console.log('🔄 [SAFEGUARD] Ignoring empty comments array - likely Firebase listener restart');
        return; // Don't update anything if we suspect this is a Firebase listener restart
      }

      // SAFEGUARD 2: Preserve unread count during temporary listener issues
      // But allow legitimate chat switches where comments should be cleared
      if (newComments.length === 0 && oldUnreadCount > 0 && hasSignificantPreviousData) {
        // Check if this might be a chat switch by looking at chat state
        const hasActiveChat = !!currentChatId;
        if (!hasActiveChat) {
          console.log('🔄 [SAFEGUARD] Preserving unread count - no active chat detected');
          return; // Preserve count if no active chat
        }
        // If we have an active chat but empty comments, this might be initial load - allow it
      }

      // DETECT CHAT SWITCH SCENARIOS
      const isChatSwitch = newComments.length > 0 && previousComments.length === 0 && currentChatId;
      const isInitialLoad = newComments.length > 0 && previousComments.length === 0 && !currentChatId;

      console.log('🔄 [CHAT_SWITCH_DETECTION]:', {
        isChatSwitch,
        isInitialLoad,
        currentChatId,
        newCommentsCount: newComments.length,
        previousCommentsCount: previousComments.length
      });

      // Store previous comments to detect new ones
      const previousCommentIds = new Set(previousComments.map(c => c.id));

      // Find new comments from other users (not current user)
      const newCommentsFromOthers = newComments.filter(comment => {
        const isNewComment = !previousCommentIds.has(comment.id);
        const isFromOtherUser = String(comment.author?.id || '') !== String(currentUserId);
        return isNewComment && isFromOtherUser;
      });

      const hasNewCommentsFromOthers = newCommentsFromOthers.length > 0;

      console.log('🔄 [NEW_COMMENTS_ANALYSIS]:', {
        previousCommentsCount: previousComments.length,
        newCommentsFromOthers: newCommentsFromOthers.length,
        hasNewCommentsFromOthers,
        newCommentIds: newCommentsFromOthers.map(c => c.id),
        isChatSwitch,
        isInitialLoad
      });

      // Update comments array - always update when we have new data
      if (newComments.length > 0) {
        state.comments = newComments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        console.log('🔄 [COMMENTS_UPDATED] Comments array updated with', newComments.length, 'comments');
      }

      // Calculate unread count for current user - recalculate when comments are available
      if (currentUserId && newComments.length > 0) {
        const unreadCount = state.comments.filter(comment => {
          // Don't count own comments as unread - handle both string and number comparison
          const commentAuthorId = String(comment.author?.id || '');
          const currentUserIdStr = String(currentUserId);

          if (commentAuthorId === currentUserIdStr) {
            console.log(`🔄 [SKIP_OWN_COMMENT] Skipping own comment ${comment.id}: author=${commentAuthorId}, user=${currentUserIdStr}`);
            return false;
          }

          // Check if user has read this comment - handle both string and number comparison
          const hasRead = comment.readBy?.some(receipt =>
            String(receipt.userId) === currentUserIdStr
          );

          if (!hasRead) {
            console.log(`🔄 [UNREAD_COMMENT] Comment ${comment.id} is UNREAD: author=${commentAuthorId}, user=${currentUserIdStr}`);
          }

          return !hasRead;
        }).length;

        console.log('🔄 [UNREAD_CALCULATION] Calculated unread count:', unreadCount, '(was:', oldUnreadCount, '), chat switch:', isChatSwitch);

        // Always update unread count when comments are loaded/refreshed
        // This ensures the count is accurate when switching between chats
        state.commentsModal.unreadCount = unreadCount;

        // Update chat-specific unread count
        if (currentChatId) {
          // Ensure chatUnreadComments is an object and initialize if needed
          if (!state.chatUnreadComments) {
            state.chatUnreadComments = {};
          }

          // Initialize the chat entry if it doesn't exist
          if (!state.chatUnreadComments[currentChatId]) {
            state.chatUnreadComments[currentChatId] = { count: 0, lastReadAt: null };
          }

          // Update the count and timestamp
          state.chatUnreadComments[currentChatId].count = unreadCount;
          state.chatUnreadComments[currentChatId].lastReadAt = new Date().toISOString();

          console.log('🔄 [CHAT_SPECIFIC_UNREAD] Updated unread count for chat', currentChatId, 'to:', unreadCount);
        }

        // Update global unread tracking for cross-chat notifications
        if (currentChatId && hasNewCommentsFromOthers) {
          console.log('🔄 [GLOBAL_UNREAD_UPDATE] Updating global unread for chat:', currentChatId);
          // This will be handled by the updateGlobalUnreadComments action
        }
      } else if (state.commentsModal.isOpen) {
        console.log('🔄 [SKIP_UNREAD_UPDATE] Skipping unread count update - comments modal is open');
      } else if (newComments.length === 0) {
        console.log('🔄 [SKIP_UNREAD_UPDATE] Skipping unread count update - no comments to process');
      }

      // Trigger sound notification ONLY for new comments from OTHER users
      if (hasNewCommentsFromOthers && !isInitialLoad) {
        // Get current chat ID from multiple sources to ensure we have it
        const notificationChatId = state.selectedChat?.id || state.selectedWhatsappChat?.id ||
                                   state.selectedChat?.thread_id || state.selectedWhatsappChat?.thread_id;
        const isCommentsModalOpen = state.commentsModal.isOpen;
        const now = Date.now();
        const timeSinceLastNotification = now - state.lastNotificationTimestamp;
        const minNotificationInterval = 2000; // 2 seconds minimum between notifications

        console.log('🔔 [NOTIFICATION_CHECK]:', {
          hasNewCommentsFromOthers,
          notificationChatId,
          selectedChatId: state.selectedChat?.id,
          selectedWhatsappChatId: state.selectedWhatsappChat?.id,
          isCommentsModalOpen,
          timeSinceLastNotification,
          minNotificationInterval,
          newCommentIds: newCommentsFromOthers.map(c => c.id),
          isChatSwitch
        });

        if (notificationChatId && !isCommentsModalOpen && timeSinceLastNotification > minNotificationInterval) {
          state.shouldPlayNotificationSound = true;
          state.lastNotificationTimestamp = now;
          console.log('🔊 [NOTIFICATION_TRIGGERED] Playing sound for new comment from another user');
        } else if (timeSinceLastNotification <= minNotificationInterval) {
          console.log('⏰ [NOTIFICATION_SKIPPED] Too soon since last notification');
        } else if (isCommentsModalOpen) {
          console.log('👁️ [NOTIFICATION_SKIPPED] Comments modal is open');
        } else if (!notificationChatId) {
          console.log('❌ [NOTIFICATION_SKIPPED] No current chat ID');
        }
      }

      // Update loading state
      state.commentsModal.loading = false;
      console.log('🔄 [COMMENTS_REALTIME_COMPLETED] Update completed successfully');
    },
  // Add action to clear chat data during transitions
  clearChatData: (state) => {
    console.log('🧹 Clearing chat data for smooth transitions');
    state.messages = [];
    state.nestedMessages = [];
    state.whatsappChatMessages = [];
    state.comments = [];
    // Don't reset unreadCount to 0 here - let it be recalculated after comments are loaded
    // state.commentsModal.unreadCount = 0;
    state.commentsModal.initialLoading = true;
    // Don't set loadingChats = true as it hides the entire chat list
    // Only set loading for messages
    state.loading.fetchingMessages = true;
  },
    markCommentsAsRead: (state, action) => {
      const { userId, userName } = action.payload;
      const currentTime = new Date().toISOString();

      state.comments = state.comments.map(comment => {
        // Check if user has already read this comment
        const hasRead = comment.readBy?.some(receipt => receipt.userId === userId);

        if (!hasRead) {
          return {
            ...comment,
            readBy: [
              ...(comment.readBy || []),
              {
                userId,
                userName,
                readAt: currentTime
              }
            ]
          };
        }
        return comment;
      });

      // Reset unread count when marking as read
      state.commentsModal.unreadCount = 0;

      // Update chat-specific unread count
      const currentChatId = state.selectedChat?.id || state.selectedWhatsappChat?.id;
      if (currentChatId && state.chatUnreadComments[currentChatId]) {
        state.chatUnreadComments[currentChatId].count = 0;
        state.chatUnreadComments[currentChatId].lastReadAt = new Date().toISOString();
      }
    },
    resetNotificationSound: (state) => {
      state.shouldPlayNotificationSound = false;
    },
    resetCommentsButtonPulse: (state) => {
      state.shouldPulseCommentsButton = false;
    },
    resetRefreshCommentsFlag: (state) => {
      state.shouldRefreshComments = false;
    },
    updateUnreadCommentCounts: (state, action) => {
      const { currentUserId } = action.payload;

      console.log(`📊 [UNREAD_COUNT] Calculating unread counts for user: ${currentUserId}`);
      console.log(`📊 [UNREAD_COUNT] Total comments to process: ${state.comments.length}`);

      if (currentUserId) {
        // Only recalculate if we have comments to process - preserve existing count if no comments loaded yet
        if (state.comments.length === 0) {
          console.log(`📊 [UNREAD_COUNT] No comments to process yet, preserving existing unread count: ${state.commentsModal.unreadCount}`);
          return;
        }

        const unreadComments = state.comments.filter(comment => {
          // Don't count own comments as unread
          if (comment.author?.id === currentUserId) {
            console.log(`👤 [UNREAD_COUNT] Skipping own comment: ${comment.id}`);
            return false;
          }

          // Check if current user has read this comment
          const hasRead = comment.readBy?.some(receipt => receipt.userId === currentUserId);
          if (!hasRead) {
            console.log(`📖 [UNREAD_COUNT] Comment ${comment.id} is unread for user ${currentUserId}`);
          }
          return !hasRead;
        });

        const unreadCount = unreadComments.length;
        console.log(`📊 [UNREAD_COUNT] Final unread count: ${unreadCount}`);

        state.commentsModal.unreadCount = unreadCount;

        // Update chat-specific unread count
        const currentChatId = state.selectedChat?.id || state.selectedWhatsappChat?.id;
        if (currentChatId) {
          if (!state.chatUnreadComments[currentChatId]) {
            state.chatUnreadComments[currentChatId] = { count: 0, lastReadAt: null };
          }
          state.chatUnreadComments[currentChatId].count = unreadCount;
        }
      }
    },
    // New action to update global unread comments across all chats
    updateGlobalUnreadComments: (state, action) => {
      const { chatId, comments, currentUserId } = action.payload;
      
      if (!currentUserId || !chatId) return;
      
      console.log(`🌍 [GLOBAL_UNREAD] Updating unread comments for chat: ${chatId}, count: ${comments.length}`);
      
      // Get previous unread count for this chat
      const previousUnreadCount = state.globalUnreadComments[chatId]?.count || 0;
      
      // Update global unread comments for this chat
      state.globalUnreadComments[chatId] = {
        count: comments.length,
        comments: comments,
        lastUpdated: new Date().toISOString()
      };
      
      // Recalculate total global unread count
      state.globalUnreadCount = Object.values(state.globalUnreadComments)
        .reduce((total, chat) => total + chat.count, 0);
        
      console.log(`🌍 [GLOBAL_UNREAD] Chat ${chatId}: ${comments.length} unread, Global total: ${state.globalUnreadCount}`);
      
      // Check if this is a new unread comment and not from current active chat
      const currentActiveChatId = state.selectedChat?.id || state.selectedWhatsappChat?.id;
      const hasNewComments = comments.length > previousUnreadCount;
      const isNotCurrentChat = chatId !== currentActiveChatId;
      
      if (hasNewComments && isNotCurrentChat) {
        // Trigger pulse effect for unread comments in other chats
        state.shouldPulseCommentsButton = true;
        console.log(`🔔 [GLOBAL_UNREAD] New unread comments in chat ${chatId} (not current chat), triggering pulse`);
      }
    },
    // Clear global unread for a specific chat when user opens it
    clearGlobalUnreadForChat: (state, action) => {
      const { chatId } = action.payload;
      
      if (state.globalUnreadComments[chatId]) {
        console.log(`🌍 [GLOBAL_UNREAD] Clearing unread for chat: ${chatId}`);
        state.globalUnreadComments[chatId] = {
          count: 0,
          comments: [],
          lastUpdated: new Date().toISOString()
        };
        
        // Recalculate total global unread count
        state.globalUnreadCount = Object.values(state.globalUnreadComments)
          .reduce((total, chat) => total + chat.count, 0);
          
        console.log(`🌍 [GLOBAL_UNREAD] New global total after clearing: ${state.globalUnreadCount}`);
      }
    },
    // Add action to initialize global unread state
    initializeGlobalUnreadComments: (state, action) => {
      const { pageId } = action.payload;
      console.log(`🌍 [GLOBAL_UNREAD] Initializing global unread for page: ${pageId}`);
      
      // Reset global state for new page
      state.globalUnreadComments = {};
      state.globalUnreadCount = 0;
      state.shouldPulseCommentsButton = false;
    },
    updateUnreadMessageCounts: (state, action) => {
      const { chatId, unreadCount } = action.payload;
      
      if (chatId) {
        if (!state.chatUnreadMessages[chatId]) {
          state.chatUnreadMessages[chatId] = { count: 0, lastReadAt: null };
        }
        state.chatUnreadMessages[chatId].count = unreadCount;
      }
    },
    markMessagesAsRead: (state, action) => {
      const { chatId } = action.payload;

      if (chatId && state.chatUnreadMessages[chatId]) {
        state.chatUnreadMessages[chatId].count = 0;
        state.chatUnreadMessages[chatId].lastReadAt = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Latest Messages
      .addCase(fetchLatestMessages.pending, (state) => {
        state.loading.fetchingLatestMessages = true;
        state.error.fetchLatestMessages = null;
      })
      .addCase(fetchLatestMessages.fulfilled, (state, action) => {
        state.loading.fetchingLatestMessages = false;
        state.messagesSnapshotUnsubscribe = action.payload.unsubscribe;
      })
      .addCase(fetchLatestMessages.rejected, (state, action) => {
        state.loading.fetchingLatestMessages = false;
        state.error.fetchLatestMessages = action.payload;
      })

      // Fetch Messages
      .addCase(fetchMessages.pending, (state) => {
        state.loading.fetchingMessages = true;
        state.error.fetchMessages = null;
        state.nestedMessages = [];
        state.messages = [];
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading.fetchingMessages = false;
        state.nestedMessages = action.payload.nestedMessages;
        state.messages = action.payload.messages;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading.fetchingMessages = false;
        state.error.fetchMessages = action.payload;
      })

      // Fetch WhatsApp Messages
      .addCase(fetchWhatsAppMessages.pending, (state) => {
        state.loading.fetchingWhatsAppMessages = true;
        state.error.fetchWhatsAppMessages = null;
      })
      .addCase(fetchWhatsAppMessages.fulfilled, (state, action) => {
        state.loading.fetchingWhatsAppMessages = false;

        // The messages are already set in the thunk, just update the chat and unsubscribe
        const { updatedChat, unsubscribe } = action.payload;

        state.selectedWhatsappChat = updatedChat;
        state.messagesSnapshotUnsubscribe = unsubscribe;
      })
      .addCase(fetchWhatsAppMessages.rejected, (state, action) => {
        state.loading.fetchingWhatsAppMessages = false;
        state.error.fetchWhatsAppMessages = action.payload;
      })

      // Fetch Messages Pagination
      .addCase(fetchMessagesPagination.pending, (state) => {
        state.loading.fetchingOlderMessages = true;
        state.messagesPagination.isLoading = true;
      })
      .addCase(fetchMessagesPagination.fulfilled, (state, action) => {
        state.loading.fetchingOlderMessages = false;
        state.messagesPagination.isLoading = false;
        
        const { messages, nextUrl, hasMore } = action.payload;
        
        // Prepend older messages to the existing messages array
        state.messagesPagination.olderMessages = [...messages, ...state.messagesPagination.olderMessages];
        state.messagesPagination.nextUrl = nextUrl;
        state.messagesPagination.hasMore = hasMore;
      })
      .addCase(fetchMessagesPagination.rejected, (state, action) => {
        state.loading.fetchingOlderMessages = false;
        state.messagesPagination.isLoading = false;
        state.error.fetchMessages = action.payload;
      })

      // Fetch WhatsApp Latest Messages
      .addCase(fetchWhatsAppLatestMessages.fulfilled, (state, action) => {
        if (action.payload) {
          const { firebaseChats, unsubscribe } = action.payload;
          state.whatsappChats = firebaseChats;
          state.messagesSnapshotUnsubscribe = unsubscribe;
        }
      })

      // Fetch WhatsApp Chats From API
      .addCase(fetchWhatsAppChatsFromAPI.fulfilled, (state, action) => {
        // API chats synced to Firebase successfully
      })

      // Select Chat Async
      .addCase(selectChatAsync.pending, (state, action) => {
        // Only clear data if selecting a different chat
        const newChatId = action.meta.arg.thread?.id;
        const currentChatId = state.selectedChat?.id;

        console.log('[REDUX_DEBUG] selectChatAsync.pending - Chat ID comparison:', {
          newChatId,
          currentChatId,
          isDifferentChat: newChatId !== currentChatId,
          willClearComments: newChatId !== currentChatId
        });

        if (newChatId !== currentChatId) {
          // Clear previous chat data only when switching to different chat
          console.log('[REDUX_DEBUG] Clearing chat data - switching to different chat');
          state.messages = [];
          state.nestedMessages = [];
          // DON'T clear comments when switching to a different chat - let the new chat load them
          // state.comments = [];
          state.selectedChat = null;
          // DON'T clear unread count - preserve it for when user returns to this chat
          // state.commentsModal.unreadCount = 0; // REMOVED - this was causing unread counts to reset
        } else {
          console.log('[REDUX_DEBUG] Preserving chat data - reselecting same chat');
          // When reselecting the same chat, don't clear anything - preserve all data
        }
        state.loading.fetchingMessages = true;
      })
      .addCase(selectChatAsync.fulfilled, (state, action) => {
        state.selectedChat = action.payload;
        state.loading.fetchingMessages = false;
      })
      .addCase(selectChatAsync.rejected, (state) => {
        state.loading.fetchingMessages = false;
      })

      // Select WhatsApp Chat Async
      .addCase(selectWhatsappChatAsync.pending, (state, action) => {
        // Only clear data if selecting a different WhatsApp chat
        const newChatId = action.meta.arg.thread?.sender_phone_number || action.meta.arg.thread?.id;
        const currentChatId = state.selectedWhatsappChat?.sender_phone_number || state.selectedWhatsappChat?.id;

        console.log('[WHATSAPP_SELECTION_DEBUG] selectWhatsappChatAsync.pending - Chat ID comparison:', {
          newChatId,
          currentChatId,
          isDifferentChat: newChatId !== currentChatId,
          willClearComments: newChatId !== currentChatId
        });

        if (newChatId !== currentChatId) {
          // Clear previous WhatsApp chat data only when switching to different chat
          console.log('[WHATSAPP_SELECTION_DEBUG] Clearing WhatsApp chat data - switching to different chat');
          state.whatsappChatMessages = [];
          state.comments = [];
          state.selectedWhatsappChat = null;
          // DON'T clear unread count - preserve it for when user returns to this chat
        } else {
          console.log('[WHATSAPP_SELECTION_DEBUG] Preserving WhatsApp chat data - reselecting same chat');
        }
        state.loading.fetchingMessages = true;
      })
      .addCase(selectWhatsappChatAsync.fulfilled, (state, action) => {
        state.selectedWhatsappChat = action.payload;
        state.loading.fetchingMessages = false;
      })
      .addCase(selectWhatsappChatAsync.rejected, (state) => {
        state.loading.fetchingMessages = false;
      })

      // Send Message
      .addCase(sendMessage.pending, (state) => {
        state.loading.sendingMessage = true;
        state.error.sendMessage = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading.sendingMessage = false;
        // The payload is now a boolean (true/false) indicating success/failure
        if (!action.payload) {
          state.error.sendMessage = "Failed to send message";
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading.sendingMessage = false;
        state.error.sendMessage = action.payload || "Failed to send message";
      })

      // Send WhatsApp Message
      .addCase(sendWhatsMessage.pending, (state) => {
        state.loading.sendingWhatsMessage = true;
        state.error.sendWhatsMessage = null;
      })
      .addCase(sendWhatsMessage.fulfilled, (state, action) => {
        state.loading.sendingWhatsMessage = false;
      })
      .addCase(sendWhatsMessage.rejected, (state, action) => {
        state.loading.sendingWhatsMessage = false;
        state.error.sendWhatsMessage = action.payload || "Failed to send WhatsApp message";
      })

      // Listen to All WhatsApp Messages
      .addCase(listenToAllWhatsappMessages.fulfilled, (state, action) => {
        // Handle the logic for listening to all WhatsApp messages
      })

      // Comments system async thunks
      .addCase(fetchComments.pending, (state) => {
        state.commentsModal.loading = true;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.commentsModal.loading = false;
        state.comments = action.payload;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.commentsModal.loading = false;
        console.error("Failed to fetch comments:", action.payload);
      })

      .addCase(addComment.pending, (state) => {
        state.commentsModal.loading = true;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        state.commentsModal.loading = false;
        state.comments.push(action.payload);

        // Note: Unread count calculation is handled by the updateUnreadCommentCounts action
        // which is dispatched from components/hooks that have access to the current user ID
      })
      .addCase(addComment.rejected, (state, action) => {
        state.commentsModal.loading = false;
        console.error("Failed to add comment:", action.payload);
      })

      .addCase(listenToComments.fulfilled, (state, action) => {
        state.commentsListeners.unsubscribe = action.payload;
      })
      .addCase(listenToComments.rejected, (state, action) => {
        console.error("Failed to set up comments listener:", action.payload);
      })

      .addCase(markCommentsAsReadAsync.fulfilled, (state, action) => {
        // The markCommentsAsRead reducer will handle the state update
        // This is just for handling the async completion
      })
      .addCase(markCommentsAsReadAsync.rejected, (state, action) => {
        console.error("Failed to mark comments as read:", action.payload);
      })

      // Start comments listener
      .addCase(startCommentsListener.pending, (state) => {
        state.commentsModal.loading = true;
      })
      .addCase(startCommentsListener.fulfilled, (state, action) => {
        state.commentsModal.loading = false;
      })
      .addCase(startCommentsListener.rejected, (state, action) => {
        state.commentsModal.loading = false;
        console.error("Failed to start comments listener:", action.payload);
      })

      // Stop comments listener
      .addCase(stopCommentsListener.fulfilled, (state) => {
        state.commentsModal.loading = false;
      })
      .addCase(stopCommentsListener.rejected, (state, action) => {
        console.error("Failed to stop comments listener:", action.payload);
      })

      // Hybrid Comments Actions
      .addCase(loadChatCommentsHybrid.pending, (state) => {
        state.commentsModal.initialLoading = true; // Use initial loading for skeleton
        state.commentsModal.loading = false; // Don't block comment input
        state.comments = []; // Clear existing comments
      })
      .addCase(loadChatCommentsHybrid.fulfilled, (state, action) => {
        state.commentsModal.initialLoading = false;
        state.commentsModal.loading = false;
        state.comments = action.payload;
      })
      .addCase(loadChatCommentsHybrid.rejected, (state, action) => {
        state.commentsModal.initialLoading = false;
        state.commentsModal.loading = false;
        console.error("Failed to load chat comments:", action.payload);
      })

      .addCase(sendCommentHybrid.pending, (state) => {
        state.commentsModal.loading = true;
      })
      .addCase(sendCommentHybrid.fulfilled, (state, action) => {
        state.commentsModal.loading = false;
        // Comment will be added via real-time listener
      })
      .addCase(sendCommentHybrid.rejected, (state, action) => {
        state.commentsModal.loading = false;
        console.error("Failed to send comment:", action.payload);
      })

      .addCase(markCommentsAsReadHybrid.fulfilled, (state, action) => {
        // Update local state immediately with read receipts for all unread comments
        const { commentIds, user } = action.payload;
        const currentTime = new Date().toISOString();

        state.comments = state.comments.map(comment => {
          // Check if this comment should be marked as read
          // Mark as read if: it's in the commentIds list OR it's unread by current user
          const shouldMarkAsRead = commentIds.includes(comment.id) ||
            (comment.author?.id !== user.id && !comment.readBy?.some(receipt => receipt.userId === user.id));

          if (shouldMarkAsRead) {
            // Check if user has already read this comment
            const hasRead = comment.readBy?.some(receipt => receipt.userId === user.id);

            if (!hasRead) {
              return {
                ...comment,
                readBy: [
                  ...(comment.readBy || []),
                  {
                    userId: user.id,
                    userName: user.name,
                    userPhoto: user.photo || null,
                    readAt: currentTime
                  }
                ]
              };
            }
          }
          return comment;
        });

        console.log('Updated local state with read receipts for user:', user.name);

        // Reset unread count since we've marked comments as read
        state.commentsModal.unreadCount = 0;

        // Note: Real-time sync will handle updating read receipts for other users
        // No need to force refresh as it causes infinite loops
      })
      .addCase(markCommentsAsReadHybrid.rejected, (state, action) => {
        console.error("Failed to mark comments as read:", action.payload);
      })

      // Enhanced Real-time Listeners
      .addCase(startRealtimeChatsListener.pending, (state) => {
        state.loading.fetchingLatestMessages = true;
      })
      .addCase(startRealtimeChatsListener.fulfilled, (state, action) => {
        state.loading.fetchingLatestMessages = false;
        console.log(`[REALTIME] Started chats listener for page ${action.payload.pageId}, active listeners: ${action.payload.listenerCount}`);
      })
      .addCase(startRealtimeChatsListener.rejected, (state, action) => {
        state.loading.fetchingLatestMessages = false;
        console.error("Failed to start real-time chats listener:", action.payload);
      })

      .addCase(startRealtimeMessagesListener.pending, (state) => {
        state.loading.fetchingMessages = true;
      })
      .addCase(startRealtimeMessagesListener.fulfilled, (state, action) => {
        state.loading.fetchingMessages = false;
        console.log(`[REALTIME] Started messages listener for chat ${action.payload.chatId}`);
      })
      .addCase(startRealtimeMessagesListener.rejected, (state, action) => {
        state.loading.fetchingMessages = false;
        console.error("Failed to start real-time messages listener:", action.payload);
      })

      .addCase(startRealtimeCommentsListener.pending, (state) => {
        state.commentsModal.loading = true;
      })
      .addCase(startRealtimeCommentsListener.fulfilled, (fulfilledState, action) => {
        fulfilledState.commentsModal.loading = false;
        console.log(`[REALTIME] Started comments listener for chat ${action.payload.chatId}, auto-mark-as-read: ${action.payload.autoMarkAsRead}`);

        // Force an initial unread count calculation when listener starts
        const currentUserId = action.meta.arg.currentUserId || (action.payload?.userId ? String(action.payload.userId) : null);
        if (currentUserId && fulfilledState.comments.length > 0) {
          const unreadCount = fulfilledState.comments.filter(comment => {
            // Don't count own comments as unread - handle both string and number comparison
            const commentAuthorId = String(comment.author?.id || '');
            const currentUserIdStr = String(currentUserId);

            if (commentAuthorId === currentUserIdStr) {
              console.log(`[UNREAD_DEBUG] Skipping own comment ${comment.id}: author=${commentAuthorId}, user=${currentUserIdStr}`);
              return false;
            }

            // Check if user has read this comment - handle both string and number comparison
            const hasRead = comment.readBy?.some(receipt =>
              String(receipt.userId) === currentUserIdStr
            );

            if (!hasRead) {
              console.log(`[UNREAD_DEBUG] Comment ${comment.id} is UNREAD: author=${commentAuthorId}, user=${currentUserIdStr}, readBy=${JSON.stringify(comment.readBy)}`);
            }

            return !hasRead;
          }).length;

          // Update unread count
          fulfilledState.commentsModal.unreadCount = unreadCount;
          console.log('Updated unread count after listener start:', fulfilledState.commentsModal.unreadCount, 'total comments:', fulfilledState.comments.length);
        }
      })
      .addCase(startRealtimeCommentsListener.rejected, (state, action) => {
        state.commentsModal.loading = false;
        console.error("Failed to start real-time comments listener:", action.payload);
      })

      .addCase(stopAllRealtimeListeners.fulfilled, (state, action) => {
        console.log(`[REALTIME] Stopped ${action.payload.cleanedUp} listeners`);
        state.latestMessagesUnsubscribe = null;
        state.messagesSnapshotUnsubscribe = null;
        state.commentsListeners.unsubscribe = null;
      })
      .addCase(stopAllRealtimeListeners.rejected, (state, action) => {
        console.error("Failed to stop real-time listeners:", action.payload);
      })

      .addCase(markCommentsAsReadRealtime.fulfilled, (state, action) => {
        console.log(`[REALTIME] Marked ${action.payload.commentIds.length} comments as read for user ${action.payload.user.name}`);
        // Real-time listener will update the state automatically
      })
      .addCase(markCommentsAsReadRealtime.rejected, (state, action) => {
        console.error("Failed to mark comments as read in real-time:", action.payload);
      })

      .addCase(getRealtimeListenerStatus.fulfilled, (state, action) => {
        console.log('[REALTIME] Listener status:', action.payload);
      });
  }
});

// Export actions
export const {
  setChats,
  setSelectedChat,
  setMessages,
  setSelectedChatStatus,
  setNestedMessages,
  setLatestMessages,
  setSelectedPage,
  setActiveFilter,
  setMessengerChats,
  setInstagramChats,
  setWhatsappChats,
  setWhatsappChatMessages,
  setWhatsAppAccounts,
  setSelectedWhatsAccount,
  setSelectedWhatsappChat,
  setWhatsAppAccountDetails,
  setSelectedPhone,
  setBusinessPhoneNumbers,
  setStartDate,
  setEndDate,
  setFilteredData,
  setConversations,
  setPaginationMeta,
  setHasMore,
  setDisabledChat,
  setDisabledChatLimit,
  setPageImg,
  setLoadingPagination,
  setLoadingChats,
  setMessagesSnapshotUnsubscribe,
  setLatestMessagesUnsubscribe,
  setPollingInterval,
  selectChat,
  selectWhatsappChat,
  handleSelectWhatsAppAccount,
  resetFilteredChatsPages,
  addNewChatsFromFirebase,
  debugMessageFlow,
  updateMessages,
  updateWhatsappChatMessages,
  updateWhatsappChatsWithFirebase,
  updateMessengerChats,
  updateInstagramChats,
  clearErrors,
  updateChats,
  updateLatestMessages,
  updateSelectedWhatsappChat,
  clearChatData,
  // Comments system actions
  setComments,
  addCommentToState,
  updateComment,
  removeComment,
  setCommentsModal,
  openCommentsModal,
  closeCommentsModal,
  setCommentsUnreadCount,
  incrementCommentsUnreadCount,
  setCommentsLoading,
  setCommentsInitialLoading,
  setCommentsListener,
  clearCommentsListener,
  updateCommentsRealtime,
  markCommentsAsRead,
  resetNotificationSound,
  resetCommentsButtonPulse,
  resetRefreshCommentsFlag,
  updateUnreadCommentCounts,
  resetChatUnreadCount,
  updateGlobalUnreadComments,
  clearGlobalUnreadForChat,
  initializeGlobalUnreadComments,
  updateUnreadMessageCounts,
  markMessagesAsRead,
  setMessagesPaginationUrl,
  resetMessagesPagination,
} = metaBusinessSuiteSlice.actions;

// Export selectors
export const selectmetaBusinessSuite = (state) => state.metaBusinessSuite;
export const selectChats = (state) => state.metaBusinessSuite.chats;
export const selectWhatsappChats = (state) => state.metaBusinessSuite.whatsappChats;
export const selectSelectedChat = (state) => state.metaBusinessSuite.selectedChat;
export const selectSelectedWhatsappChat = (state) => state.metaBusinessSuite.selectedWhatsappChat;
export const selectMessages = createSelector(
  [state => state.metaBusinessSuite.nestedMessages, state => state.metaBusinessSuite.messages],
  (nestedMessages, messages) => [
    ...(nestedMessages || []),
    ...(messages || [])
  ]
);
export const selectChatStatus = (state) => state.metaBusinessSuite.selectedChatStatus;
export const selectLatestMessages = (state) => state.metaBusinessSuite.latestMessages;
export const selectWhatsappChatMessages = (state) => state.metaBusinessSuite.whatsappChatMessages;
export const selectSelectedPage = (state) => state.metaBusinessSuite.selectedPage;
export const selectActiveFilter = (state) => state.metaBusinessSuite.activeFilter;
export const selectLoadingStates = (state) => state.metaBusinessSuite.loading;
export const selectErrorStates = (state) => state.metaBusinessSuite.error;
export const selectDisabledChat = (state) => state.metaBusinessSuite.disabledChat;
export const selectDisabledChatLimit = (state) => state.metaBusinessSuite.disabledChatLimit;
export const selectPaginationMeta = (state) => state.metaBusinessSuite.paginationMeta;
export const selectLoadingPagination = (state) => state.metaBusinessSuite.loadingPagination;
export const selectInstagramChats = (state) => state.metaBusinessSuite.instagramChats;
export const selectMessengerChats = (state) => state.metaBusinessSuite.messengerChats;
export const selectLoadingChats = (state) => state.metaBusinessSuite.loadingChats;
export const selectHasMore = (state) => state.metaBusinessSuite.hasMore;
export const selectSelectedWhatsAccount = (state) => state.metaBusinessSuite.selectedWhatsAccount;
export const selectSelectedPhone = (state) => state.metaBusinessSuite.selectedPhone;
export const selectWhatsAppAccounts = (state) => state.metaBusinessSuite.whatsAppAccounts;
export const selectWhatsappLoading = (state) => state.metaBusinessSuite.loading;

// Comments system selectors
export const selectComments = (state) => state.metaBusinessSuite.comments;
export const selectCommentsModal = (state) => state.metaBusinessSuite.commentsModal;
export const selectCommentsUnreadCount = (state) => {
  // Return global unread count if no specific chat is selected, otherwise current chat unread count
  const globalCount = state.metaBusinessSuite.globalUnreadCount || 0;
  const currentChatCount = state.metaBusinessSuite.commentsModal.unreadCount || 0;
  const selectedChatId = state.metaBusinessSuite.selectedChat?.id || state.metaBusinessSuite.selectedWhatsappChat?.id;
  
  // If a chat is selected, show its specific count, otherwise show global count
  return selectedChatId ? currentChatCount : globalCount;
};
export const selectCommentsLoading = (state) => state.metaBusinessSuite.commentsModal.loading;
export const selectCommentsInitialLoading = (state) => state.metaBusinessSuite.commentsModal.initialLoading;
export const selectCommentsModalOpen = (state) => state.metaBusinessSuite.commentsModal.isOpen;
export const selectCommentsListener = (state) => state.metaBusinessSuite.commentsListeners.unsubscribe;
export const selectChatUnreadComments = (state) => state.metaBusinessSuite.chatUnreadComments;
export const selectChatUnreadMessages = (state) => state.metaBusinessSuite.chatUnreadMessages;
export const selectShouldPlayNotificationSound = (state) => state.metaBusinessSuite.shouldPlayNotificationSound;
export const selectShouldRefreshComments = (state) => state.metaBusinessSuite.shouldRefreshComments;
// Global unread selectors
export const selectGlobalUnreadComments = (state) => state.metaBusinessSuite.globalUnreadComments;
export const selectGlobalUnreadCount = (state) => state.metaBusinessSuite.globalUnreadCount;
export const selectGlobalChatUnreadCount = (chatId) => (state) => state.metaBusinessSuite.globalUnreadComments[chatId]?.count || 0;
export const selectShouldPulseCommentsButton = (state) => state.metaBusinessSuite.shouldPulseCommentsButton;

// Messages pagination selectors
export const selectMessagesPagination = (state) => state.metaBusinessSuite.messagesPagination || {};
export const selectMessagesPaginationNextUrl = (state) => state.metaBusinessSuite.messagesPagination?.nextUrl || null;
export const selectMessagesPaginationHasMore = (state) => state.metaBusinessSuite.messagesPagination?.hasMore || false;
export const selectMessagesPaginationLoading = (state) => state.metaBusinessSuite.messagesPagination?.isLoading || false;
export const selectOlderMessages = (state) => state.metaBusinessSuite.messagesPagination?.olderMessages || [];

// Combined messages selector that includes paginated older messages
export const selectAllMessages = createSelector(
  [selectOlderMessages, selectMessages],
  (olderMessages, currentMessages) => [
    ...(olderMessages || []),
    ...(currentMessages || [])
  ]
);

// Memoized selector for comments with read status for current user
export const selectCommentsWithReadStatus = createSelector(
  [selectComments, (state, userId) => userId],
  (comments, userId) => {
    return comments.map(comment => ({
      ...comment,
      isReadByCurrentUser: comment.readBy?.some(receipt => receipt.userId === userId) || false,
      readByCount: comment.readBy?.length || 0
    }));
  }
);

// Memoized selector for unread comments count for current user
export const selectUnreadCommentsCount = createSelector(
  [selectComments, (state, userId) => userId],
  (comments, userId) => {
    return comments.filter(comment =>
      !comment.readBy?.some(receipt => receipt.userId === userId)
    ).length;
  }
);

// Selector for unread count of a specific chat
export const selectChatUnreadCount = createSelector(
  [selectChatUnreadComments, (state, chatId) => chatId],
  (chatUnreadComments, chatId) => {
    return chatUnreadComments[chatId]?.count || 0;
  }
);

// Selector for unread message count of a specific chat
export const selectChatUnreadMessageCount = createSelector(
  [selectChatUnreadMessages, (state, chatId) => chatId],
  (chatUnreadMessages, chatId) => {
    return chatUnreadMessages[chatId]?.count || 0;
  }
);

// Memoized selector that merges latest messages with chats (same as Context API)
export const selectMergedAndSortedChats = createSelector(
  [
    state => state.metaBusinessSuite.activeFilter,
    state => state.metaBusinessSuite.messengerChats,
    state => state.metaBusinessSuite.instagramChats,
    selectLatestMessages
  ],
  (activeFilter, messengerChats, instagramChats, latestMessages) => {
    if (!activeFilter) {
      return [];
    }

    let baseChats = [];
    if (activeFilter === 'messenger') {
      baseChats = messengerChats || [];
    } else if (activeFilter === 'instagram') {
      baseChats = instagramChats || [];
    } else {
      return [];
    }

    if (!Array.isArray(baseChats) || baseChats.length === 0) {
      return baseChats;
    }

    // Use the same logic as the Context API
    const merged = baseChats.map(chat => {
      if (!chat || !chat.participants || !chat.participants.data) {
        return chat;
      }

      // Get the participant ID (same logic as Context)
      const participantIndex = activeFilter === 'instagram' ? 1 : 0;
      const participantId = chat.participants.data[participantIndex]?.id;

      console.log(`[MERGE_DEBUG] Chat ${chat.id} (${activeFilter}) - Extracting participant:`, {
        participantIndex,
        participantId,
        participantIdType: typeof participantId,
        allParticipants: chat.participants.data?.map(p => ({ id: p?.id, name: p?.name })),
        chatHasMessage: !!chat.message,
        originalChatMsg: chat.message?.substring(0, 50)
      });

      if (!participantId) {
        console.log(`[MERGE_DEBUG] No participant ID found for chat ${chat.id}, returning original chat`);
        return chat;
      }

      // Use the same extractLatestMessage function as Context
      const latestMsg = extractLatestMessage(participantId, latestMessages);

      console.log(`[MERGE_DEBUG] Chat ${chat.id} (${activeFilter}) - Final result:`, {
        participantId,
        hasLatestMsg: !!latestMsg,
        latestMsgContent: latestMsg?.message?.substring(0, 50),
        latestMsgTime: latestMsg?.created_time,
        originalChatMsg: chat.message?.substring(0, 50),
        originalChatTime: chat.updated_time || chat.created_time,
        willUseLatestMsg: !!latestMsg
      });

      if (latestMsg) {
        return {
          ...chat,
          message: latestMsg.message,
          // Use the chat document's updated_time for proper sorting
          updated_time: latestMsg.updated_time || latestMsg.created_time,
          created_time: latestMsg.created_time,
          type: latestMsg.type || chat.type,
          url: latestMsg.url || chat.url,
        };
      }
      return chat;
    });

    // Sort by updated_time or created_time (newest first)
    const sorted = merged.sort((a, b) => {
      const timeA = new Date(a.updated_time || a.created_time);
      const timeB = new Date(b.updated_time || b.created_time);
      return timeB - timeA; // Descending order (newest first)
    });

    console.log(`[CHAT_SORTING_DEBUG] Sorted ${sorted.length} chats by updated_time:`,
      sorted.slice(0, 3).map(chat => ({
        id: chat.id,
        message: chat.message?.substring(0, 30),
        updated_time: chat.updated_time,
        sortTime: new Date(chat.updated_time || chat.created_time).toISOString()
      }))
    );

    return sorted;
  }
);

// Memoized selector for WhatsApp chats that merges with latest messages
export const selectMergedAndSortedWhatsappChats = createSelector(
  [
    state => state.metaBusinessSuite.whatsappChats,
    selectLatestMessages
  ],
  (whatsappChats, latestMessages) => {
    if (!Array.isArray(whatsappChats) || whatsappChats.length === 0) {
      return whatsappChats;
    }

    // Merge WhatsApp chats with latest messages
    const merged = whatsappChats.map(chat => {
      if (!chat) {
        return chat;
      }

      // For WhatsApp, we need to find the latest message for this chat
      // The chat has sender_phone_number, and messages have 'sender' or 'from' field
      const chatPhoneNumber = chat.sender_phone_number;

      if (!chatPhoneNumber) {
        return chat;
      }

      // Find all messages for this WhatsApp chat
      const chatMessages = latestMessages?.filter(msg => {
        // Check if the message is from or to this chat
        // Handle both 'sender' and 'from' fields for compatibility
        const msgSender = msg.sender || msg.from;
        const msgRecipient = msg.recipient;

        const isMatch = msgSender === chatPhoneNumber || msgRecipient === chatPhoneNumber;
        return isMatch;
      });

      if (chatMessages && chatMessages.length > 0) {
        // Find the latest message
        const latestMsg = chatMessages.sort((a, b) => {
          const dateA = new Date(a.created_time || a.created_at);
          const dateB = new Date(b.created_time || b.created_at);
          return dateB - dateA; // Sort in descending order
        })[0];

        return {
          ...chat,
          last_message: {
            message: latestMsg.message,
            created_at: latestMsg.created_time || latestMsg.created_at,
            updated_at: latestMsg.created_time || latestMsg.created_at,
            from: latestMsg.sender || latestMsg.from,
            type: latestMsg.type || "text",
            message_id: latestMsg.message_id,
            timestamp: Math.floor(new Date(latestMsg.created_time || latestMsg.created_at).getTime() / 1000).toString(),
            url: latestMsg.url || null
          },
          updated_at: latestMsg.created_time || latestMsg.created_at,
        };
      }
      return chat;
    });

    // Sort by updated_at or created_at
    const sorted = merged.sort((a, b) => {
      const dateA = a.last_message?.created_at || a.updated_at || a.created_at;
      const dateB = b.last_message?.created_at || b.updated_at || b.created_at;
      return new Date(dateB) - new Date(dateA);
    });

    return sorted;
  }
);

// Helper function to format date (moved from context)
export const formatDate = (timestamp) => {
  let messageDate;
  if (timestamp?.seconds && timestamp?.nanoseconds) {
    const milliseconds =
      timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6;
    messageDate = new Date(milliseconds);
  } else {
    try {
      messageDate = new Date(timestamp);
    } catch (error) {
      console.error("Error parsing timestamp:", error);
    }
  }

  const currentDate = new Date();
  return formatTimestamp(messageDate, currentDate);
};

export default metaBusinessSuiteSlice.reducer;
